import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Alert, Chip, TextField, MenuItem, Grid, Stack,
  Divider, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Checkbox, FormControlLabel,
  FormGroup, IconButton, Link as MuiLink, Menu, ListItemIcon, ListItemText,
  Tooltip, Select, OutlinedInput, InputLabel, FormControl,
  Drawer, List, ListItemButton, useTheme, useMediaQuery,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import NotesIcon from '@mui/icons-material/Notes';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PsychologyIcon from '@mui/icons-material/Psychology';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ArchiveIcon from '@mui/icons-material/Archive';
import BlockIcon from '@mui/icons-material/Block';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailReadOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UndoIcon from '@mui/icons-material/Undo';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VerifiedIcon from '@mui/icons-material/Verified';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PublicIcon from '@mui/icons-material/Public';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonIcon from '@mui/icons-material/Person';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getLead, getLeads, deleteLead, convertLead,
  unsubscribeLead, resubscribeLead, unsubscribeLeadContact, resubscribeLeadContact,
  deleteLeadContact, createLeadContact,
  getUsers,
  createLeadInteraction, updateLeadInteraction,
  findContacts,
  getContactProviders,
  aiEnrichLead, submitEnrichmentReview, scoreLead, updateLeadAiInsights, generateLeadOutreachHint,
  getInteractionTimeline, addInteractionNote, getLeadOutreachTimeline,
  generateLinkedInMessage, analyzeLinkedInReply, logSentLinkedInMessage,
  processInboundManual,
  getLeadSignals, updateLeadSignalStatus, deleteLeadSignal,
  getLeadSegmentationMembers, removeLeadSegmentationMember,
  getLeadFollowUps, completeFollowUp,
  getLeadMeetings, createMeeting, deleteMeeting, cancelMeeting,
  getAiProviders,
  getInteractionTemplates, personalizeEmail,
  getCurrentUser, getAiPromptTemplatesByCategory,
  updateLead, addLeadNote,
  getLeadTasks, createLeadTask, completeTask, reopenTask, deleteTask,
  getLeadAnalyticsEvents,
  cancelEmailQueueItem,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import IcpProfilePanel from '../components/IcpProfilePanel';
import {
  LEAD_STATUS_OPTIONS as STATUS_OPTIONS,
  LEAD_STATUS_COLORS as STATUS_COLORS,
  LEAD_CONTACT_STATUS_COLORS,
} from '../constants/leadStatus';

const TAG_PALETTE = [
  { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  { bg: '#fce7f3', text: '#9f1239', border: '#fbcfe8' },
  { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' },
  { bg: '#ccfbf1', text: '#115e59', border: '#99f6e4' },
  { bg: '#ffedd5', text: '#9a3412', border: '#fdba74' },
  { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe' },
];

function parseOutreachHint(text) {
  if (!text) return { items: [], rest: '' };
  const lines = text.split(/\r?\n/);
  const items = [];
  const restLines = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^[*\-•]\s*\*\*([^*]+?):?\*\*\s*(.*)$/);
    if (m) {
      items.push({ label: m[1].trim(), value: m[2].trim() });
      continue;
    }
    const m2 = line.match(/^[*\-•]\s+(.*)$/);
    if (m2) {
      items.push({ label: null, value: m2[1].replace(/\*\*/g, '').trim() });
      continue;
    }
    restLines.push(line.replace(/\*\*/g, ''));
  }
  return { items, rest: restLines.join('\n') };
}

function OutreachHintBody({ text }) {
  const { items, rest } = parseOutreachHint(text);
  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#1f2937', whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    );
  }
  const dim = (v) => /^(n\/?a|none|wait|watch only|tbd|unknown)$/i.test(v.trim());
  return (
    <Box>
      <Stack spacing={1}>
        {items.map((it, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%',
              bgcolor: dim(it.value) ? '#fdba74' : '#ea580c',
              mt: 0.85, flexShrink: 0,
            }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {it.label && (
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9a3412', letterSpacing: 0.3, textTransform: 'uppercase', lineHeight: 1.2 }}>
                  {it.label}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: dim(it.value) ? '#9a3412' : '#1f2937',
                  fontStyle: dim(it.value) ? 'italic' : 'normal',
                  opacity: dim(it.value) ? 0.7 : 1,
                  fontWeight: it.label ? 500 : 400,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {it.value || '—'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
      {rest && (
        <Typography variant="body2" sx={{ mt: 1.5, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
          {rest}
        </Typography>
      )}
    </Box>
  );
}

function tagColor(label) {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = ((h << 5) - h + label.charCodeAt(i)) | 0;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

function TimelineBodyView({ body, channel }) {
  const [expanded, setExpanded] = useState(false);
  if (!body) return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  const isEmail = channel === 'Email' && isHtml;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        sx={{ textTransform: 'none', minWidth: 0, p: 0, fontSize: '0.75rem' }}
      >
        {expanded ? '▾ Hide message' : '▸ View full message'}
      </Button>
      {expanded && (
        <Paper
          variant="outlined"
          sx={{ mt: 0.5, p: 0, overflow: 'hidden', bgcolor: '#fff' }}
        >
          {isEmail ? (
            <iframe
              title="Sent message"
              srcDoc={body}
              sandbox=""
              style={{ width: '100%', height: 360, border: 'none', display: 'block', backgroundColor: '#fff' }}
            />
          ) : (
            <Typography
              sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', p: 1.5, fontFamily: isHtml ? 'monospace' : 'inherit' }}
            >
              {body}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

const TASK_TYPE_LABELS = {
  FindContacts: 'Find contacts',
  ProfileLead: 'Profile lead',
  SendEmail: 'Send email',
  LinkedInOutreach: 'LinkedIn outreach',
  XingOutreach: 'Xing outreach',
  Call: 'Call',
  Meeting: 'Meeting',
  Research: 'Research',
  FollowUp: 'Follow up',
  Custom: 'Task',
};

function TaskTypeIcon({ type }) {
  const sx = { fontSize: 18 };
  switch (type) {
    case 'FindContacts': return <PersonSearchIcon sx={{ ...sx, color: '#0891b2' }} />;
    case 'ProfileLead': return <PsychologyIcon sx={{ ...sx, color: '#7c3aed' }} />;
    case 'SendEmail': return <EmailIcon sx={{ ...sx, color: '#3182ce' }} />;
    case 'LinkedInOutreach': return <LinkedInIcon sx={{ ...sx, color: '#0077b5' }} />;
    case 'XingOutreach': return <GroupIcon sx={{ ...sx, color: '#0698A0' }} />;
    case 'Call': return <PhoneIcon sx={{ ...sx, color: '#38a169' }} />;
    case 'Meeting': return <VideoCallIcon sx={{ ...sx, color: '#805ad5' }} />;
    case 'Research': return <ManageSearchIcon sx={{ ...sx, color: '#475569' }} />;
    case 'FollowUp': return <ScheduleIcon sx={{ ...sx, color: '#dd6b20' }} />;
    default: return <AssignmentIcon sx={{ ...sx, color: '#64748b' }} />;
  }
}

function TasksPanel({ tasks, loading, showCompleted, onToggleCompleted, onAdd, onComplete, onReopen, onDelete }) {
  const open = (tasks || []).filter(t => t.status !== 'Done' && t.status !== 'Cancelled');
  const done = (tasks || []).filter(t => t.status === 'Done' || t.status === 'Cancelled');
  const visible = showCompleted ? [...open, ...done] : open;

  const fmtDue = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    const now = new Date();
    const overdue = dt < now;
    return { text: dt.toLocaleDateString(), overdue };
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AssignmentIcon sx={{ fontSize: 18 }} /> Tasks
          <Chip size="small" label={open.length} sx={{ height: 18, fontSize: '0.7rem' }} />
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Checkbox size="small" checked={showCompleted} onChange={onToggleCompleted} />}
            label={<Typography variant="caption">Show completed</Typography>}
          />
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd}
            sx={{ borderColor: '#e2e8f0', color: '#475569' }}>
            Add task
          </Button>
        </Stack>
      </Stack>

      {loading && <Typography variant="caption" color="text.secondary">Loading…</Typography>}
      {!loading && visible.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          No tasks yet. Use “Add task” to plan your next steps.
        </Typography>
      )}

      <Stack spacing={0.75}>
        {visible.map(t => {
          const due = fmtDue(t.dueDate);
          const isDone = t.status === 'Done' || t.status === 'Cancelled';
          return (
            <Paper
              key={t.id}
              variant="outlined"
              sx={{
                p: 1.25, borderRadius: 1.5,
                bgcolor: isDone ? '#f8fafc' : '#fff',
                opacity: isDone ? 0.75 : 1,
                borderColor: due?.overdue && !isDone ? '#fca5a5' : '#e2e8f0',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={() => isDone ? onReopen(t.id) : onComplete(t)}>
                  {isDone ? <UndoIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                </IconButton>
                <TaskTypeIcon type={t.type} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500, textDecoration: isDone ? 'line-through' : 'none', fontSize: '0.875rem' }}>
                    {t.title}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                    <Chip size="small" label={TASK_TYPE_LABELS[t.type] || t.type}
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                    {due && (
                      <Typography variant="caption" sx={{ color: due.overdue && !isDone ? '#dc2626' : '#64748b' }}>
                        Due {due.text}{due.overdue && !isDone ? ' · overdue' : ''}
                      </Typography>
                    )}
                    {t.assignedToName && (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>· {t.assignedToName}</Typography>
                    )}
                    {t.completionInteractionId && (
                      <Tooltip title="Activity logged on completion">
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
                <IconButton size="small" onClick={() => onDelete(t.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

const TAB_KEYS = ['overview', 'profile', 'contacts', 'insights', 'icp', 'activity', 'outreach'];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initialTab = (() => {
    const t = searchParams.get('tab');
    const idx = TAB_KEYS.indexOf(t);
    return idx >= 0 ? idx : 0;
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [contactsPage, setContactsPage] = useState(0);
  const [contactsRowsPerPage, setContactsRowsPerPage] = useState(10);

  const themeMq = useTheme();
  const isMobile = useMediaQuery(themeMq.breakpoints.down('sm'));
  const [tabDrawerOpen, setTabDrawerOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Contact state
  const [contactModal, setContactModal] = useState(null);
  const [contactForm, setContactForm] = useState({});

  // Contact discovery state
  const [discovering, setDiscovering] = useState(false);
  const discoverAbortRef = useRef(null);
  const [discoveredContacts, setDiscoveredContacts] = useState(null);
  const [savingContact, setSavingContact] = useState(null);
  const [selectedDiscovered, setSelectedDiscovered] = useState([]);
  const [contactProviders, setContactProviders] = useState([]);
  const [selectedContactProviders, setSelectedContactProviders] = useState([]);

  // Convert state
  const [convertModal, setConvertModal] = useState(false);
  const [convertExistingId, setConvertExistingId] = useState('');
  const [convertNotes, setConvertNotes] = useState('');

  // Interaction state
  const [interactionModal, setInteractionModal] = useState(null);
  const [interactionForm, setInteractionForm] = useState({});

  // Enrich & Score state
  const [enriching, setEnriching] = useState(false);
  const enrichAbortRef = useRef(null);
  const [reviewData, setReviewData] = useState(null);
  const [enrichmentDecisions, setEnrichmentDecisions] = useState({});
  const [reviewModalTab, setReviewModalTab] = useState(0);
  const [autoApplyError, setAutoApplyError] = useState(null);
  const [insightsEditing, setInsightsEditing] = useState(false);
  const [insightsDraft, setInsightsDraft] = useState('');
  const [insightsSaving, setInsightsSaving] = useState(false);
  const [outreachHintLoading, setOutreachHintLoading] = useState(false);
  const [scoringLead, setScoringLead] = useState(false);
  const [enrichAiProvider, setEnrichAiProvider] = useState('openai');

  // Timeline state
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [timelineLoading, setTimelineLoading] = useState(false);

  // ICP Profile tab state — extracted signals + segmentation memberships
  const [icpData, setIcpData] = useState({ signals: [], segments: [] });
  const [icpLoading, setIcpLoading] = useState(false);

  // LinkedIn assist state
  const [linkedInPanel, setLinkedInPanel] = useState(false);
  const [linkedInContact, setLinkedInContact] = useState('');
  const [linkedInStage, setLinkedInStage] = useState('FirstContact');
  const [linkedInMsg, setLinkedInMsg] = useState(null);
  const [linkedInGenerating, setLinkedInGenerating] = useState(false);
  const [linkedInReply, setLinkedInReply] = useState('');
  const [linkedInAnalysis, setLinkedInAnalysis] = useState(null);
  const [linkedInAnalyzing, setLinkedInAnalyzing] = useState(false);
  const [linkedInAiProvider, setLinkedInAiProvider] = useState('openai');
  const [aiProviders, setAiProviders] = useState([]);
  const [linkedInTemplates, setLinkedInTemplates] = useState([]);
  const [linkedInTemplateId, setLinkedInTemplateId] = useState('');
  const [linkedInPersonalizing, setLinkedInPersonalizing] = useState(false);
  const [linkedInPersonalizePreview, setLinkedInPersonalizePreview] = useState(null);
  const [linkedInOpeningTone, setLinkedInOpeningTone] = useState('');
  const [linkedInImprovePrompts, setLinkedInImprovePrompts] = useState([]);
  const [linkedInImprovePromptId, setLinkedInImprovePromptId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Inbound email state
  const [inboundModal, setInboundModal] = useState(false);
  const [inboundForm, setInboundForm] = useState({ fromEmail: '', subject: '', body: '', contactId: '' });
  const [inboundProcessing, setInboundProcessing] = useState(false);
  const [inboundResult, setInboundResult] = useState(null);

  // Note state
  const [noteText, setNoteText] = useState('');

  // Quick actions state
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [activityActionsAnchor, setActivityActionsAnchor] = useState(null);
  const [leadIds, setLeadIds] = useState([]);
  const [navStatuses, setNavStatuses] = useState(() => {
    try {
      const raw = sessionStorage.getItem('leadsNavStatuses');
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [addingTag, setAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [ownerMenuAnchor, setOwnerMenuAnchor] = useState(null);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [parkBusy, setParkBusy] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickNoteText, setQuickNoteText] = useState('');
  const [quickNoteSaving, setQuickNoteSaving] = useState(false);
  const [taskModal, setTaskModal] = useState(null); // null | { type, ... }
  const [taskForm, setTaskForm] = useState({ type: 'Custom', title: '', description: '', dueDate: '', assignedToUserId: '' });
  const [taskSaving, setTaskSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [completeTaskDialog, setCompleteTaskDialog] = useState(null); // null | { task }
  const [completeLogActivity, setCompleteLogActivity] = useState(true);
  const [completeNotes, setCompleteNotes] = useState('');

  // Follow-ups state
  const [followUps, setFollowUps] = useState([]);

  // Marketing-site visit activity (azotte-analytics tracker)
  const [marketingEvents, setMarketingEvents] = useState([]);

  // Meetings state
  const [meetings, setMeetings] = useState([]);
  const [meetingModal, setMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ title: '', description: '', startTime: '', endTime: '', location: '', leadContactId: '', notes: '', attendeeUserIds: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [linkedInLogging, setLinkedInLogging] = useState(false);
  const [linkedInLogged, setLinkedInLogged] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getLead(id);
      setLead(data);
    } catch { setError('Failed to load lead'); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const fetchIcpProfile = useCallback(async () => {
    if (!id) return;
    setIcpLoading(true);
    try {
      const [sigRes, segRes] = await Promise.all([
        getLeadSignals(id),
        getLeadSegmentationMembers(id),
      ]);
      setIcpData({ signals: sigRes.data || [], segments: segRes.data || [] });
    } catch {
      setError('Failed to load ICP profile data');
    } finally {
      setIcpLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 4) fetchIcpProfile();
  }, [activeTab, fetchIcpProfile]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getLeadAnalyticsEvents(id, { limit: 20 })
      .then(res => { if (alive) setMarketingEvents(res.data || []); })
      .catch(() => { /* admin-only or no data — ignore silently */ });
    return () => { alive = false; };
  }, [id]);
  useEffect(() => {
    let alive = true;
    if (navStatuses.length > 0) {
      getLeads({ includeArchived: true })
        .then(res => {
          if (!alive) return;
          const set = new Set(navStatuses);
          const ids = (res.data || []).filter(l => set.has(l.status)).map(l => l.id);
          setLeadIds(ids);
        })
        .catch(() => {});
      return () => { alive = false; };
    }
    let cached = null;
    try {
      const raw = sessionStorage.getItem('leadsListIds');
      if (raw) cached = JSON.parse(raw);
    } catch { /* ignore */ }
    if (Array.isArray(cached) && cached.length > 0) {
      setLeadIds(cached);
      return () => { alive = false; };
    }
    getLeads({ includeArchived: false })
      .then(res => {
        if (!alive) return;
        const ids = (res.data || []).map(l => l.id);
        setLeadIds(ids);
        try { sessionStorage.setItem('leadsListIds', JSON.stringify(ids)); } catch { /* ignore */ }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [navStatuses]);
  useEffect(() => {
    getContactProviders().then(res => {
      const providers = res.data || [];
      setContactProviders(providers);
      setSelectedContactProviders(providers.filter(p => p.isDefault && p.isAvailable).map(p => p.name));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    getAiProviders().then(res => {
      const list = (res.data || []).filter(p => p.isAvailable);
      setAiProviders(list);
      const openai = list.find(p => (p.name || '').toLowerCase() === 'openai');
      if (openai) {
        setLinkedInAiProvider('openai');
        setEnrichAiProvider('openai');
      } else if (list.length > 0) {
        setLinkedInAiProvider(list[0].name);
        setEnrichAiProvider(list[0].name);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    const params = {
      channel: 'LinkedIn',
      stage: linkedInStage,
      recommendedForLeadId: parseInt(id),
    };
    getInteractionTemplates(params).then(res => {
      setLinkedInTemplates((res.data || []).filter(t => t.isActive));
    }).catch(() => {});
  }, [id, linkedInStage]);

  useEffect(() => {
    getCurrentUser().then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!id) return;
    try {
      setTasksLoading(true);
      const { data } = await getLeadTasks(id);
      setTasks(data || []);
    } catch { /* silent */ }
    finally { setTasksLoading(false); }
  }, [id]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    const cats = ['ImproveOutreach', 'AdjustTone', 'PersonalizeTemplate', 'GenerateLinkedIn'];
    Promise.all(cats.map(c => getAiPromptTemplatesByCategory(c).catch(() => ({ data: [] }))))
      .then(results => {
        const merged = [];
        results.forEach((r, i) => {
          (r.data || []).filter(t => t.isActive).forEach(t => merged.push({ ...t, _category: cats[i] }));
        });
        setLinkedInImprovePrompts(merged);
      });
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const formatRelative = (d) => {
    if (!d) return 'Never';
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    return formatDate(d);
  };

  // Delete
  const handleDelete = async () => {
    if (!(await dialog.confirm({ title: 'Delete lead', message: 'Delete this lead permanently?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteLead(id);
      navigate('/leads');
    } catch { setError('Failed to delete'); }
  };

  // Convert to customer
  const handleConvert = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await convertLead(id, {
        notes: convertNotes || null,
        existingCustomerId: convertExistingId ? parseInt(convertExistingId) : null,
      });
      setConvertModal(false);
      if (data.success && data.customerId) {
        await dialog.alert({ title: 'Converted to customer', message: `Converted to customer: ${data.customerName}\n${data.contactsCopied} contacts, ${data.interactionsCopied} interactions copied.` });
        navigate(`/customers/${data.customerId}`);
      } else {
        setError(data.errorMessage || 'Conversion failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Conversion failed');
    }
  };

  // Contact handlers
  const handleDeleteContact = async (contactId) => {
    if (!(await dialog.confirm({ title: 'Delete contact', message: 'Delete this contact?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteLeadContact(id, contactId);
      fetchLead();
    } catch { setError('Failed to delete contact'); }
  };

  // Mailing list opt-out (lead-level)
  const handleUnsubscribeLead = async () => {
    const reason = await dialog.prompt({
      title: 'Unsubscribe lead',
      message: `Suppress ALL outreach to ${lead?.companyName || 'this company'}. Pending Draft / Scheduled / ReadyToSend emails for the company will be cancelled.`,
      label: 'Reason (optional)',
      confirmText: 'Unsubscribe', danger: true,
    });
    if (reason === null) return;
    try {
      const { data } = await unsubscribeLead(id, reason || null);
      await fetchLead();
      if (data?.cancelledInteractionCount > 0) {
        await dialog.alert({ title: 'Unsubscribed', message: `Cancelled ${data.cancelledInteractionCount} pending email(s).` });
      }
    } catch (err) { setError(err.response?.data?.message || 'Failed to unsubscribe lead'); }
  };

  const handleResubscribeLead = async () => {
    if (!(await dialog.confirm({ title: 'Resubscribe lead', message: 'Allow outreach to this company again? Cancelled emails are NOT restored.', confirmText: 'Resubscribe' }))) return;
    try { await resubscribeLead(id); fetchLead(); }
    catch (err) { setError(err.response?.data?.message || 'Failed to resubscribe lead'); }
  };

  // Mailing list opt-out (contact-level)
  const handleUnsubscribeContact = async (contact) => {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || `Contact #${contact.id}`;
    const reason = await dialog.prompt({
      title: 'Unsubscribe contact',
      message: `Suppress outreach to ${name}. Pending emails to this contact will be cancelled.`,
      label: 'Reason (optional)',
      confirmText: 'Unsubscribe', danger: true,
    });
    if (reason === null) return;
    try {
      const { data } = await unsubscribeLeadContact(id, contact.id, reason || null);
      await fetchLead();
      if (data?.cancelledInteractionCount > 0) {
        await dialog.alert({ title: 'Unsubscribed', message: `Cancelled ${data.cancelledInteractionCount} pending email(s).` });
      }
    } catch (err) { setError(err.response?.data?.message || 'Failed to unsubscribe contact'); }
  };

  const handleResubscribeContact = async (contact) => {
    if (!(await dialog.confirm({ title: 'Resubscribe contact', message: 'Allow outreach to this contact again?', confirmText: 'Resubscribe' }))) return;
    try { await resubscribeLeadContact(id, contact.id); fetchLead(); }
    catch (err) { setError(err.response?.data?.message || 'Failed to resubscribe contact'); }
  };

  const handleFindContacts = async () => {
    setDiscovering(true);
    setDiscoveredContacts(null);
    setError('');
    const controller = new AbortController();
    discoverAbortRef.current = controller;
    try {
      const { data } = await findContacts(
        { leadId: parseInt(id), providerNames: selectedContactProviders.length > 0 ? selectedContactProviders : undefined },
        { signal: controller.signal }
      );
      setDiscoveredContacts(data.contacts || []);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError') {
        // canceled by user — silent
      } else {
        setError(err.response?.data?.message || 'Failed to discover contacts');
      }
    } finally {
      discoverAbortRef.current = null;
      setDiscovering(false);
    }
  };

  const handleCancelDiscover = () => {
    if (discoverAbortRef.current) {
      discoverAbortRef.current.abort();
    }
  };

  const handleAddDiscoveredContact = async (contact) => {
    setSavingContact(contact);
    try {
      await createLeadContact(id, {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        jobTitle: contact.jobTitle,
        linkedInUrl: contact.linkedInUrl,
        notes: `Discovered via ${contact.source} (confidence: ${contact.confidence}%)`,
        contactInfos: []
      });
      // Remove from discovered list
      setDiscoveredContacts(prev => prev.filter(c => c !== contact));
      fetchLead();
    } catch {
      setError('Failed to add contact');
    } finally {
      setSavingContact(null);
    }
  };

  // Enrich & Score handlers
  // After enrichment runs, every returned suggestion is auto-accepted server-side and the modal opens
  // as a read-only preview with two tabs (Insights, Applied fields). The previous accept/reject UI was
  // pure friction — users were clicking Accept All every time. If a value turns out wrong it's still
  // editable from the regular Profile/Lead Info forms.
  const handleEnrich = async () => {
    setEnriching(true);
    setReviewData(null);
    setEnrichmentDecisions({});
    setAutoApplyError(null);
    setReviewModalTab(0);
    const controller = new AbortController();
    enrichAbortRef.current = controller;
    try {
      const { data } = await aiEnrichLead(id, enrichAiProvider || undefined, controller.signal);
      setReviewData(data);

      const suggestions = data?.suggestions || [];
      if (suggestions.length > 0) {
        const decisions = suggestions.map(s => ({
          fieldName: s.fieldName,
          action: 'Accept',
          editedValue: s.suggestedValue,
        }));
        try {
          await submitEnrichmentReview({ leadId: data.leadId, decisions });
          const applied = {};
          suggestions.forEach(s => { applied[s.fieldName] = 'accepted'; });
          setEnrichmentDecisions(applied);
          fetchLead();
        } catch (applyErr) {
          setAutoApplyError(applyErr.response?.data?.message || 'Auto-apply failed — values shown but not saved.');
        }
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled') {
        // user-cancelled: silent
      } else {
        setError(err.response?.data?.message || 'Enrichment failed');
      }
    } finally {
      enrichAbortRef.current = null;
      setEnriching(false);
    }
  };

  const cancelEnrich = () => {
    if (enrichAbortRef.current) enrichAbortRef.current.abort();
  };

  const handleRefreshOutreachHint = async () => {
    setOutreachHintLoading(true);
    try {
      await generateLeadOutreachHint(id, enrichAiProvider || undefined);
      fetchLead();
    } catch (err) {
      setError(err.response?.data?.message || 'Hint generation failed');
    } finally {
      setOutreachHintLoading(false);
    }
  };

  const closeReviewModal = () => {
    setReviewData(null);
    setEnrichmentDecisions({});
    setReviewModalTab(0);
    setAutoApplyError(null);
  };

  const handleScore = async () => {
    setScoringLead(true);
    try {
      await scoreLead(id);
      fetchLead();
    } catch (err) { setError(err.response?.data?.message || 'Scoring failed'); }
    finally { setScoringLead(false); }
  };

  const confColor = (c) => c >= 0.8 ? '#38a169' : c >= 0.5 ? '#dd6b20' : '#e53e3e';

  const LEAD_INFO_LABELS = {
    website: 'Website',
    phone: 'Phone',
    email: 'Email',
    linkedInUrl: 'LinkedIn URL',
    description: 'Description',
    address: 'Address',
    city: 'City',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country',
    countryCode: 'Country Code',
    headcount: 'Headcount',
    companyType: 'Company Type',
  };

  const enrichmentFieldLabel = (key) => {
    if (!key) return key;
    if (key.startsWith('_lead.')) {
      const col = key.slice(6);
      return `Lead › ${LEAD_INFO_LABELS[col] || col}`;
    }
    return key;
  };

  const renderInsightsInline = (s) => {
    const escaped = s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  };

  const renderInsightsMarkdown = (md) => {
    if (!md) return null;
    return md.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <Typography key={i} variant="subtitle2" sx={{ fontWeight: 700, mt: i === 0 ? 0 : 1.25, mb: 0.5, color: '#1f2937' }}>{line.slice(3)}</Typography>;
      }
      if (line.startsWith('# ')) {
        return <Typography key={i} variant="subtitle1" sx={{ fontWeight: 700, mt: i === 0 ? 0 : 1.25, mb: 0.5 }}>{line.slice(2)}</Typography>;
      }
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <Box key={i} sx={{ display: 'flex', gap: 1, ml: 0.5, fontSize: '0.85rem', lineHeight: 1.6, color: '#1f2937' }}>
            <span>•</span>
            <span dangerouslySetInnerHTML={{ __html: renderInsightsInline(line.slice(2)) }} />
          </Box>
        );
      }
      if (!line.trim()) return <Box key={i} sx={{ height: 6 }} />;
      return (
        <Typography key={i} variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: '#1f2937' }}
          dangerouslySetInnerHTML={{ __html: renderInsightsInline(line) }} />
      );
    });
  };

  // Timeline helpers
  const fetchTimeline = async (filter) => {
    setTimelineLoading(true);
    try {
      const params = {};
      const f = filter || timelineFilter;
      if (f && f !== 'all') params.channel = f;
      const { data } = await getInteractionTimeline(id, params);
      setTimeline(data || []);
    } catch { setError('Failed to load timeline'); }
    finally { setTimelineLoading(false); }
  };

  const fetchFollowUps = async () => {
    try {
      const { data } = await getLeadFollowUps(id);
      setFollowUps(data || []);
    } catch { /* silent */ }
  };

  const fetchMeetings = async () => {
    try {
      const { data } = await getLeadMeetings(id);
      setMeetings(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 5 && id) {
      fetchTimeline();
      fetchFollowUps();
      fetchMeetings();
    }
  }, [activeTab, id]);

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!lead) return <Box sx={{ p: 5, textAlign: 'center' }}><Typography>Lead not found</Typography></Box>;

  const handleCompleteFollowUp = async (interactionId) => {
    try {
      await completeFollowUp(interactionId, 'lead');
      fetchFollowUps();
    } catch { setError('Failed to complete follow-up'); }
  };

  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    try {
      await createMeeting({
        ...meetingForm,
        leadId: parseInt(id),
        leadContactId: meetingForm.leadContactId ? parseInt(meetingForm.leadContactId) : null,
        attendeeUserIds: meetingForm.attendeeUserIds.map(Number),
      });
      setMeetingModal(false);
      setMeetingForm({ title: '', description: '', startTime: '', endTime: '', location: '', leadContactId: '', notes: '', attendeeUserIds: [] });
      fetchMeetings();
    } catch (err) { setError(err.response?.data?.message || 'Failed to schedule meeting'); }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!(await dialog.confirm({ title: 'Cancel meeting', message: 'Cancel this meeting?', confirmText: 'Cancel meeting' }))) return;
    try {
      await cancelMeeting(meetingId);
      fetchMeetings();
    } catch { setError('Failed to cancel meeting'); }
  };

  const handleCancelScheduledEmail = async (interactionId) => {
    if (!(await dialog.confirm({
      title: 'Cancel scheduled email',
      message: 'Remove this scheduled email from the queue? It will not be sent.',
      confirmText: 'Cancel email',
      danger: true,
    }))) return;
    try {
      await cancelEmailQueueItem(interactionId);
      fetchTimeline();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || 'Failed to cancel scheduled email');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!(await dialog.confirm({ title: 'Delete meeting', message: 'Delete this meeting permanently?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteMeeting(meetingId);
      fetchMeetings();
    } catch { setError('Failed to delete meeting'); }
  };

  const openMeetingModal = async () => {
    if (allUsers.length === 0) {
      try {
        const { data } = await getUsers();
        setAllUsers(data || []);
      } catch { /* silent */ }
    }
    setMeetingModal(true);
  };

  const generateIcsUrl = (m) => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `DTSTART:${fmt(m.startTime)}`, `DTEND:${fmt(m.endTime)}`,
      `SUMMARY:${m.title}`, `DESCRIPTION:${(m.description || '').replace(/\n/g, '\\n')}`,
      `LOCATION:${m.location || ''}`,
      ...m.attendees.map(a => `ATTENDEE;CN=${a.fullName}:mailto:${a.email}`),
      'END:VEVENT', 'END:VCALENDAR'
    ];
    return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
  };

  const channelIcon = (ch) => {
    if (ch === 'Email') return <EmailIcon fontSize="small" sx={{ color: '#3182ce' }} />;
    if (ch === 'LinkedInManual') return <LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} />;
    if (ch === 'Call') return <PhoneIcon fontSize="small" sx={{ color: '#38a169' }} />;
    if (ch === 'Meeting') return <EventIcon fontSize="small" sx={{ color: '#805ad5' }} />;
    return <NotesIcon fontSize="small" sx={{ color: '#718096' }} />;
  };

  const directionColor = (dir) => {
    if (dir === 'Outbound') return '#3182ce';
    if (dir === 'Inbound') return '#38a169';
    return '#718096';
  };

  const statusColor = (st) => {
    const map = { Draft: '#718096', Scheduled: '#d69e2e', ReadyToSend: '#dd6b20', Sent: '#3182ce', Delivered: '#38a169', Opened: '#805ad5', Replied: '#2f855a', Failed: '#e53e3e', Archived: '#a0aec0' };
    return map[st] || '#718096';
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addInteractionNote(id, { content: noteText });
      setNoteText('');
      fetchTimeline();
    } catch { setError('Failed to add note'); }
  };

  // ─── Quick actions ──────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    setStatusMenuAnchor(null);
    if (newStatus === lead.status) return;
    setStatusUpdating(true);
    try {
      await updateLead(id, { status: newStatus });
      await fetchLead();
    } catch (err) {
      setError(err.response?.data?.message || 'Status update failed');
    } finally { setStatusUpdating(false); }
  };

  const handlePark = async () => {
    if (!(await dialog.confirm({ title: 'Park lead', message: 'Set this lead to Nurture? It will be hidden from active workbench queues.', confirmText: 'Nurture' }))) return;
    setParkBusy(true);
    try {
      // Outreach state unification: legacy Archived/Parked merged into Nurture.
      await updateLead(id, { status: 'Nurture', nurtureReason: 'Manually parked' });
      await fetchLead();
    } catch (err) {
      setError(err.response?.data?.message || 'Park failed');
    } finally { setParkBusy(false); }
  };

  const handleUnpark = async () => {
    setParkBusy(true);
    try {
      await updateLead(id, { status: 'New' });
      await fetchLead();
    } catch (err) {
      setError(err.response?.data?.message || 'Unpark failed');
    } finally { setParkBusy(false); }
  };

  const parseTags = (raw) => (raw || '').split(',').map(t => t.trim()).filter(Boolean);

  const saveTags = async (next) => {
    setTagSaving(true);
    try {
      const joined = next.join(', ');
      await updateLead(id, { tags: joined });
      setLead(l => ({ ...l, tags: joined }));
    } catch (err) {
      setError(err.response?.data?.message || 'Tag update failed');
    } finally { setTagSaving(false); }
  };

  const handleAddTag = async () => {
    const newTag = tagInput.trim().replace(/,/g, '');
    setAddingTag(false);
    setTagInput('');
    if (!newTag) return;
    const current = parseTags(lead.tags);
    if (current.some(t => t.toLowerCase() === newTag.toLowerCase())) return;
    await saveTags([...current, newTag]);
  };

  const handleRemoveTag = async (tag) => {
    const current = parseTags(lead.tags);
    await saveTags(current.filter(t => t !== tag));
  };

  const handleOpenOwnerMenu = (e) => {
    setOwnerMenuAnchor(e.currentTarget);
    if (allUsers.length === 0) {
      getUsers().then(({ data }) => setAllUsers(data || [])).catch(() => {});
    }
  };

  const handleAssignOwner = async (userId) => {
    setOwnerMenuAnchor(null);
    if ((userId || null) === (lead.assignedToUserId || null)) return;
    setOwnerSaving(true);
    try {
      // Backend treats 0 as "unassign" sentinel since null is ignored by .HasValue check.
      await updateLead(id, { assignedToUserId: userId == null ? 0 : userId });
      await fetchLead();
    } catch (err) {
      setError(err.response?.data?.message || 'Owner update failed');
    } finally { setOwnerSaving(false); }
  };

  const handleQuickNoteSave = async () => {
    if (!quickNoteText.trim()) return;
    setQuickNoteSaving(true);
    try {
      await addLeadNote(id, quickNoteText.trim());
      setQuickNoteText('');
      setQuickNoteOpen(false);
      if (timeline.length > 0) fetchTimeline();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add note');
    } finally { setQuickNoteSaving(false); }
  };

  const openTaskModal = (type) => {
    setTaskMenuAnchor(null);
    setTaskForm({
      type,
      title: '',
      description: '',
      dueDate: '',
      assignedToUserId: currentUser?.id ? String(currentUser.id) : '',
    });
    setTaskModal({ mode: 'create', type });
    if (allUsers.length === 0) {
      getUsers().then(({ data }) => setAllUsers(data || [])).catch(() => {});
    }
  };

  const handleTaskSave = async () => {
    setTaskSaving(true);
    try {
      const payload = {
        type: taskForm.type,
        title: taskForm.title || null,
        description: taskForm.description || null,
        dueDate: taskForm.dueDate ? new Date(taskForm.dueDate).toISOString() : null,
        assignedToUserId: taskForm.assignedToUserId ? parseInt(taskForm.assignedToUserId) : null,
      };
      await createLeadTask(id, payload);
      setTaskModal(null);
      await fetchTasks();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create task');
    } finally { setTaskSaving(false); }
  };

  const handleTaskDelete = async (taskId) => {
    if (!(await dialog.confirm({ title: 'Delete task', message: 'Delete this task?', confirmText: 'Delete', danger: true }))) return;
    try { await deleteTask(taskId); await fetchTasks(); }
    catch { setError('Failed to delete task'); }
  };

  const handleTaskReopen = async (taskId) => {
    try { await reopenTask(taskId); await fetchTasks(); }
    catch { setError('Failed to reopen task'); }
  };

  const openCompleteDialog = (task) => {
    setCompleteLogActivity(['SendEmail', 'LinkedInOutreach', 'XingOutreach', 'Call', 'Meeting'].includes(task.type));
    setCompleteNotes('');
    setCompleteTaskDialog({ task });
  };

  const handleCompleteConfirm = async () => {
    const task = completeTaskDialog?.task;
    if (!task) return;
    try {
      await completeTask(task.id, { logActivity: completeLogActivity, notes: completeNotes || null });
      setCompleteTaskDialog(null);
      await fetchTasks();
      await fetchLead(); // status may auto-transition
      if (timeline.length > 0) fetchTimeline();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete task');
    }
  };

  const handleGenerateLinkedIn = async () => {
    if (!linkedInContact) { setError('Select a contact'); return; }
    setLinkedInGenerating(true);
    setLinkedInMsg(null);
    setLinkedInLogged(false);
    try {
      const tpl = linkedInTemplates.find(t => String(t.id) === String(linkedInTemplateId));
      // Pick opening based on tone if template has openings
      let opening = null;
      if (tpl?.openings && linkedInOpeningTone) {
        try {
          const arr = JSON.parse(tpl.openings);
          opening = (arr || []).find(o => o.tone === linkedInOpeningTone);
        } catch {}
      }
      const baseLines = [];
      if (opening?.text) baseLines.push(`Open with this line: ${opening.text}`);
      if (tpl?.bodyTemplate) baseLines.push(`Use this template body as base:\n${tpl.bodyTemplate}`);
      if (linkedInImprovePromptId) {
        const p = linkedInImprovePrompts.find(x => String(x.id) === String(linkedInImprovePromptId));
        if (p?.systemPrompt) baseLines.push(`Style guidance: ${p.systemPrompt}`);
      }

      const { data } = await generateLinkedInMessage({
        leadId: parseInt(id),
        contactId: parseInt(linkedInContact),
        stage: linkedInStage,
        aiProviderName: linkedInAiProvider,
        context: baseLines.length > 0 ? baseLines.join('\n\n') : null,
      });
      setLinkedInMsg(data);
    } catch (err) { setError(err.response?.data?.message || 'LinkedIn message generation failed'); }
    finally { setLinkedInGenerating(false); }
  };

  const handlePersonalizeLinkedIn = async () => {
    if (!linkedInMsg?.message) { setError('Generate or pick a template first'); return; }
    setLinkedInPersonalizing(true);
    setError('');
    try {
      const { data } = await personalizeEmail({
        leadId: parseInt(id),
        contactId: linkedInContact ? parseInt(linkedInContact) : null,
        subject: linkedInMsg.subject || '',
        body: linkedInMsg.message,
        channel: 'LinkedIn',
        aiProviderName: linkedInAiProvider,
      });
      setLinkedInPersonalizePreview(data);
    } catch (err) {
      const detail = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage
        || err.response?.data?.message || err.response?.data || err.message;
      setError(`Personalize failed: ${detail}`);
    } finally {
      setLinkedInPersonalizing(false);
    }
  };

  const handleLogSentLinkedIn = async () => {
    if (!linkedInMsg?.message) { setError('No message to log'); return; }
    if (!linkedInContact) { setError('Select a contact'); return; }
    setLinkedInLogging(true);
    setLinkedInLogged(false);
    try {
      await logSentLinkedInMessage({
        leadId: parseInt(id),
        contactId: parseInt(linkedInContact),
        messageType: 'FirstMessage',
        stage: linkedInStage,
        body: linkedInMsg.message,
        notes: linkedInMsg.subject || null,
      });
      setLinkedInLogged(true);
      fetchTimeline();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || 'Failed to log message');
    } finally {
      setLinkedInLogging(false);
    }
  };

  const handleApplyLinkedInPersonalize = () => {
    if (linkedInPersonalizePreview && linkedInMsg) {
      setLinkedInMsg({
        ...linkedInMsg,
        subject: linkedInPersonalizePreview.personalizedSubject || linkedInMsg.subject,
        message: linkedInPersonalizePreview.personalizedBody || linkedInMsg.message,
      });
      setLinkedInPersonalizePreview(null);
      setLinkedInLogged(false);
    }
  };

  const handleAnalyzeLinkedInReply = async () => {
    if (!linkedInReply.trim()) { setError('Paste a reply to analyze'); return; }
    setLinkedInAnalyzing(true);
    setLinkedInAnalysis(null);
    try {
      const { data } = await analyzeLinkedInReply({
        leadId: parseInt(id),
        contactId: linkedInContact ? parseInt(linkedInContact) : null,
        replyText: linkedInReply,
        stage: linkedInStage,
        aiProviderName: linkedInAiProvider,
      });
      setLinkedInAnalysis(data);
    } catch (err) { setError(err.response?.data?.message || 'Analysis failed'); }
    finally { setLinkedInAnalyzing(false); }
  };

  const handleInboundSubmit = async () => {
    setInboundProcessing(true);
    try {
      const { data } = await processInboundManual({
        leadId: parseInt(id),
        contactId: inboundForm.contactId ? parseInt(inboundForm.contactId) : null,
        fromEmail: inboundForm.fromEmail,
        subject: inboundForm.subject,
        body: inboundForm.body,
      });
      setInboundResult(data);
      setInboundForm({ fromEmail: '', subject: '', body: '', contactId: '' });
      fetchTimeline();
      fetchLead();
      fetchFollowUps();
    } catch (err) { setError(err.response?.data?.message || 'Processing failed'); }
    finally { setInboundProcessing(false); }
  };

  // Parse profile data
  let profileData = {};
  if (lead.profileData) {
    try { profileData = JSON.parse(lead.profileData); } catch {}
  }

  // AzotteGrid columns for main contacts table
  const contactColumns = [
    {
      key: 'outreachScore', header: 'Score',
      render: (val, row) => {
        const s = val ?? 0;
        const color = s >= 120 ? '#16a34a' : s >= 80 ? '#0284c7' : s >= 40 ? '#f59e0b' : '#94a3b8';
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ fontWeight: 700, color, minWidth: 28, textAlign: 'right' }}>{s}</Box>
            {row.isRecommendedForOutreach && (
              <Tooltip title="Top-N pick from contact discovery">
                <Chip label="Winner" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      key: 'name', header: 'Name', render: (_, row) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '-'}</Typography>
          {row.seniorityLevel && row.seniorityLevel !== 'Unknown' && (
            <Typography variant="caption" sx={{ color: '#64748b' }}>{row.seniorityLevel}</Typography>
          )}
        </Box>
      ),
    },
    { key: 'jobTitle', header: 'Job Title', render: (val) => val || '-', hideOnMobile: true },
    {
      key: 'email', header: 'Email', hideOnMobile: true,
      render: (val, row) => {
        if (!val) return '-';
        return row.isUnsubscribed
          ? <Box>
              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>{val}</Typography>
              <Chip label="Unsubscribed" size="small" sx={{ height: 16, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#b91c1c', mt: 0.25 }} />
            </Box>
          : val;
      },
    },
    { key: 'phone', header: 'Phone', render: (val) => val || '-', hideOnMobile: true, hideOnTablet: true },
    {
      key: 'discoverySource', header: 'Source', hideOnMobile: true, hideOnTablet: true,
      render: (val) => val
        ? <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>{val}</Typography>
        : '-',
    },
    {
      key: 'status', header: 'Status', hideOnMobile: true,
      render: (val) => {
        if (!val) return '-';
        const c = LEAD_CONTACT_STATUS_COLORS[val] || { bg: '#f3f4f6', text: '#4b5563' };
        return <Chip label={val} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: c.bg, color: c.text }} />;
      },
    },
    { key: 'isPrimary', header: 'Primary', render: (val) => val ? <Chip label="Primary" color="primary" size="small" /> : '-' },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => navigate(`/leads/${id}/contacts/${row.id}`)}>Edit</Button>
          {row.isUnsubscribed ? (
            <Tooltip title="Resubscribe contact">
              <Button size="small" variant="outlined" color="success" startIcon={<MarkEmailReadIcon sx={{ fontSize: 16 }} />}
                onClick={() => handleResubscribeContact(row)}>
                Resubscribe
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Unsubscribe contact from mailing list">
              <Button size="small" variant="outlined" color="warning" startIcon={<BlockIcon sx={{ fontSize: 16 }} />}
                onClick={() => handleUnsubscribeContact(row)}>
                Unsubscribe
              </Button>
            </Tooltip>
          )}
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteContact(row.id)}>Delete</Button>
        </Stack>
      )
    },
  ];

  // Sorted by outreach score desc, then primary, then last name — winners surface at top
  const sortedContacts = [...(lead.contacts || [])].sort((a, b) => {
    const sa = a.outreachScore ?? 0;
    const sb = b.outreachScore ?? 0;
    if (sb !== sa) return sb - sa;
    if (!!b.isPrimary !== !!a.isPrimary) return (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0);
    return (a.lastName || '').localeCompare(b.lastName || '');
  });

  // AzotteGrid columns for discovered contacts table
  const discoveredColumns = [
    {
      key: 'select', header: () => (
        <Checkbox size="small"
          checked={discoveredContacts && selectedDiscovered.length === discoveredContacts.length}
          onChange={() => setSelectedDiscovered(prev => prev.length === discoveredContacts.length ? [] : discoveredContacts.map((_, i) => i))}
        />
      ),
      render: (_, row) => {
        const idx = discoveredContacts.indexOf(row);
        return (
          <Checkbox size="small"
            checked={selectedDiscovered.includes(idx)}
            onChange={() => setSelectedDiscovered(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx])}
          />
        );
      }
    },
    { key: 'name', header: 'Name', render: (_, row) => <Typography sx={{ fontWeight: 500 }}>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '-'}</Typography> },
    { key: 'jobTitle', header: 'Job Title', render: (val) => val || '-', hideOnMobile: true },
    {
      key: 'isDecisionMaker', header: 'Role', render: (val) =>
        val ? <Chip label="Decision Maker" color="primary" size="small" /> : <Chip label="Generic" size="small" />
    },
    { key: 'email', header: 'Email', render: (val) => val || <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>Not found</Typography>, hideOnMobile: true },
    {
      key: 'linkedInUrl', header: 'LinkedIn', hideOnMobile: true, render: (val) => val
        ? <a href={val} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} />
          </a>
        : <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
    },
    { key: 'source', header: 'Source', render: (val) => <Chip label={val} size="small" variant="outlined" />, hideOnMobile: true, hideOnTablet: true },
    {
      key: 'confidence', header: 'Confidence', render: (val) => (
        <Typography sx={{ color: val >= 80 ? '#38a169' : val >= 50 ? '#dd6b20' : '#e53e3e', fontWeight: 600 }}>
          {val}%
        </Typography>
      )
    },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <Button size="small" variant="contained" onClick={() => handleAddDiscoveredContact(row)} disabled={savingContact === row}>
          {savingContact === row ? 'Adding...' : '+ Add'}
        </Button>
      )
    },
  ];

  const sidebarStatus = STATUS_COLORS[lead?.status] || STATUS_COLORS.New;
  const sidebarRegion = lead?.country || lead?.state || lead?.city || '-';
  const sidebarWebsite = lead?.website ? lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  const summarySidebar = lead ? (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', position: { md: 'sticky' }, top: { md: 16 } }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Lead Overview
        </Typography>
        <Chip
          size="small"
          label={lead.status}
          sx={{ bgcolor: sidebarStatus.bg, color: sidebarStatus.text, border: `1px solid ${sidebarStatus.border}`, fontWeight: 600, height: 20 }}
        />
      </Stack>

      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
          {lead.companyName}
        </Typography>
        {sidebarWebsite && (
          <MuiLink
            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
            target="_blank" rel="noreferrer"
            sx={{ fontSize: '0.75rem', color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            {sidebarWebsite} <OpenInNewIcon sx={{ fontSize: 12 }} />
          </MuiLink>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={1}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Lead Score</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
            {lead.azotteFitScore ?? '-'}<Typography component="span" sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>/100</Typography>
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>ICP Match</Typography>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
            {lead.icpMatchPercentage != null ? `${lead.icpMatchPercentage}%` : '-'}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Enrichment</Typography>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
            {lead.enrichmentCompleteness != null ? `${lead.enrichmentCompleteness}%` : 'Not yet'}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={1}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Owner</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>{lead.assignedToName || 'Unassigned'}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Source</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>
            {lead.source || '-'}{lead.sourceDetail ? ` · ${lead.sourceDetail}` : ''}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Region</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>{sidebarRegion}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Industry</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>{lead.industryName || '-'}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Industry Group</Typography>
          {lead.industryGroupName ? (
            <Chip
              size="small"
              label={lead.industryGroupName}
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: 22,
                ...(lead.industryGroupColor ? { borderColor: lead.industryGroupColor, color: lead.industryGroupColor } : {}),
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>-</Typography>
          )}
        </Box>

        {lead.headcount != null && (
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>Headcount</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#0f172a' }}>{lead.headcount}</Typography>
          </Box>
        )}
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={1}>
        {lead.email && (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <EmailIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.email}</Typography>
          </Stack>
        )}
        {lead.phone && (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <PhoneIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#0f172a' }}>{lead.phone}</Typography>
          </Stack>
        )}
        {lead.linkedInUrl && (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <LinkedInIcon sx={{ fontSize: 14, color: '#0077b5' }} />
            <MuiLink
              href={lead.linkedInUrl.startsWith('http') ? lead.linkedInUrl : `https://${lead.linkedInUrl}`}
              target="_blank" rel="noreferrer"
              sx={{ fontSize: '0.8rem' }}
            >
              LinkedIn
            </MuiLink>
          </Stack>
        )}
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <GroupIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#0f172a' }}>
            {lead.contacts?.length || 0} contact{(lead.contacts?.length || 0) === 1 ? '' : 's'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <CalendarMonthIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
            Created {formatDate(lead.createdAt)}
          </Typography>
        </Stack>
      </Stack>

      {lead.tags && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500, mb: 0.5 }}>Tags</Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {String(lead.tags).split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
              <Chip key={i} label={t} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            ))}
          </Stack>
        </>
      )}
    </Paper>
  ) : null;

  return (
    <Box>
      {/* Back link + prev/next */}
      {(() => {
        const currentId = parseInt(id, 10);
        const idx = leadIds.indexOf(currentId);
        const prevId = idx > 0 ? leadIds[idx - 1] : null;
        const nextId = idx >= 0 && idx < leadIds.length - 1 ? leadIds[idx + 1] : null;
        const positionLabel = idx >= 0 && leadIds.length > 0 ? `${idx + 1} of ${leadIds.length}` : null;
        return (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/leads')}
              sx={{ color: '#64748b', fontWeight: 500, px: 1, minWidth: 0, '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Back to Leads List</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Back</Box>
            </Button>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Tooltip title={prevId ? 'Previous lead' : 'No previous lead'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!prevId}
                    onClick={() => prevId && navigate(`/leads/${prevId}`)}
                    sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, width: 32, height: 32, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {positionLabel && (
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500, px: 1, minWidth: 60, textAlign: 'center' }}>
                  {positionLabel}
                </Typography>
              )}
              <Tooltip title={nextId ? 'Next lead' : 'No next lead'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!nextId}
                    onClick={() => nextId && navigate(`/leads/${nextId}`)}
                    sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, width: 32, height: 32, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 180 }, ml: { xs: 0.5, sm: 1 } }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Filter by status</InputLabel>
                <Select
                  multiple
                  value={navStatuses}
                  onChange={(e) => {
                    const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setNavStatuses(v);
                    try { sessionStorage.setItem('leadsNavStatuses', JSON.stringify(v)); } catch { /* ignore */ }
                  }}
                  input={<OutlinedInput label="Filter by status" />}
                  renderValue={(selected) => selected.length === 0 ? 'All' : selected.length === 1 ? (STATUS_OPTIONS.find(o => o.value === selected[0])?.label || selected[0]) : `${selected.length} selected`}
                  sx={{ fontSize: '0.75rem', height: 32, '& .MuiSelect-select': { py: 0.5 } }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} dense>
                      <Checkbox size="small" checked={navStatuses.includes(opt.value)} />
                      <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                    </MenuItem>
                  ))}
                  {navStatuses.length > 0 && (
                    <Box sx={{ px: 1, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
                      <Button size="small" fullWidth onClick={() => {
                        setNavStatuses([]);
                        try { sessionStorage.setItem('leadsNavStatuses', '[]'); } catch { /* ignore */ }
                      }}>Clear</Button>
                    </Box>
                  )}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        );
      })()}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {lead.isCompanySuppressed && (
        <Alert
          severity="warning"
          icon={<BlockIcon fontSize="small" />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleResubscribeLead}>
              Lift suppression
            </Button>
          }
        >
          <Box sx={{ fontWeight: 600 }}>
            Company suppressed — no outreach allowed
            {lead.companySuppressedAt && (
              <Box component="span" sx={{ fontWeight: 400, ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                · {new Date(lead.companySuppressedAt).toLocaleString()}
              </Box>
            )}
          </Box>
          {lead.companySuppressionReason && (
            <Box sx={{ fontSize: '0.8rem', mt: 0.5 }}>
              Reason: {lead.companySuppressionReason}
            </Box>
          )}
          <Box sx={{ fontSize: '0.75rem', mt: 0.25, color: 'text.secondary' }}>
            Outreach is blocked at send time. Auto Outreach jobs skip this lead.
          </Box>
        </Alert>
      )}

      {/* Header */}
      {(() => {
        const initials = (lead.companyName || '?')
          .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
        const score = lead.azotteFitScore;
        const scoreColor = score == null ? '#94a3b8' : score >= 75 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
        const enrichVerified = (lead.enrichmentCompleteness ?? 0) >= 50;
        const lastEnrichedLabel = enrichVerified ? `${lead.enrichmentCompleteness}% complete` : 'Not yet';
        const statusC = STATUS_COLORS[lead.status] || STATUS_COLORS.New;
        const cleanWebsite = lead.website ? lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
        const region = lead.country || lead.state || lead.city || 'Unknown';
        const closeMore = () => setMoreMenuAnchor(null);

        const MetaItem = ({ icon, label, value }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: '#94a3b8', lineHeight: 1.1, fontWeight: 500 }}>{label}:</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#0f172a', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
              </Typography>
            </Box>
          </Box>
        );

        return (
        <Paper
          elevation={0}
          sx={{ mb: 2, borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden', bgcolor: '#fff' }}
        >
          {/* Top row */}
          <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', gap: { xs: 1.5, md: 2.5 }, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {/* Avatar */}
            <Box sx={{
              width: { xs: 56, md: 76 }, height: { xs: 56, md: 76 }, borderRadius: 2.5,
              background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, border: '1px solid #e0e7ff',
            }}>
              <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.75rem' }, fontWeight: 800, color: '#4f46e5', letterSpacing: '-0.02em' }}>
                {initials}
              </Typography>
            </Box>

            {/* Center: identity + chips */}
            <Box sx={{ flex: 1, minWidth: 0, width: { xs: 'calc(100% - 72px)', md: 'auto' } }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 0.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.625rem' }, fontWeight: 700, color: '#0f172a', lineHeight: 1.15, letterSpacing: '-0.01em', wordBreak: 'break-word' }}>
                  {lead.companyName}
                </Typography>
                {enrichVerified && (
                  <Tooltip title="Verified — enrichment data on file">
                    <VerifiedIcon sx={{ color: '#16a34a', fontSize: 22 }} />
                  </Tooltip>
                )}
              </Stack>
              {cleanWebsite && (
                <Box
                  component="a"
                  href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', color: '#6366f1', textDecoration: 'none', mb: 1.25, '&:hover': { textDecoration: 'underline' } }}
                >
                  {cleanWebsite}
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Box>
              )}
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Chip
                  icon={<GroupIcon sx={{ fontSize: 14, color: `${statusC.text} !important` }} />}
                  label={statusUpdating ? 'Updating…' : lead.status}
                  size="small"
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                  clickable
                  sx={{ bgcolor: statusC.bg, color: statusC.text, border: `1px solid ${statusC.border}`, fontWeight: 600, fontSize: '0.75rem', height: 28, borderRadius: 1.5, '& .MuiChip-icon': { ml: 0.75 } }}
                />
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor)}
                  onClose={() => setStatusMenuAnchor(null)}
                  PaperProps={{ sx: { maxWidth: 320 } }}
                >
                  {STATUS_OPTIONS.map(opt => {
                    const c = STATUS_COLORS[opt.value] || STATUS_COLORS.New;
                    return (
                      <MenuItem
                        key={opt.value}
                        selected={opt.value === lead.status}
                        onClick={() => handleStatusChange(opt.value)}
                        sx={{ alignItems: 'flex-start', py: 1, whiteSpace: 'normal' }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.text, flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.35, pl: 1.75 }}>
                            {opt.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Menu>
                <Chip
                  icon={<DescriptionOutlinedIcon sx={{ fontSize: 14, color: '#64748b !important' }} />}
                  label={lead.source}
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontWeight: 600, fontSize: '0.75rem', height: 28, borderRadius: 1.5, '& .MuiChip-icon': { ml: 0.75 } }}
                />
                {lead.azotteFitScore != null && (
                  <Chip label={`Fit ${lead.azotteFitScore}`} size="small"
                    sx={{ bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontWeight: 700, fontSize: '0.75rem', height: 28, borderRadius: 1.5 }} />
                )}
                {parseTags(lead.tags).map(t => {
                  const c = tagColor(t);
                  return (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onDelete={tagSaving ? undefined : () => handleRemoveTag(t)}
                      sx={{
                        bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`,
                        fontWeight: 600, fontSize: '0.75rem', height: 28, borderRadius: 1.5,
                        '& .MuiChip-deleteIcon': { fontSize: 14, color: c.text, opacity: 0.6, '&:hover': { opacity: 1, color: c.text } },
                      }}
                    />
                  );
                })}
                {addingTag && !isMobile ? (
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="e.g. FTA, OTT, SVOD"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
                      if (e.key === 'Escape') { setTagInput(''); setAddingTag(false); }
                    }}
                    onBlur={handleAddTag}
                    disabled={tagSaving}
                    sx={{
                      width: 140,
                      '& .MuiOutlinedInput-root': { height: 28, fontSize: '0.75rem', borderRadius: 1.5, bgcolor: '#fff' },
                      '& .MuiOutlinedInput-input': { py: 0, px: 1.25 },
                    }}
                  />
                ) : (
                  <Tooltip title="Add hint tag (e.g. FTA, OTT, SVOD)">
                    <Chip
                      icon={<AddIcon sx={{ fontSize: 14, color: '#64748b !important' }} />}
                      label="Tag"
                      size="small"
                      onClick={() => { if (isMobile) { setTagInput(''); setTagDialogOpen(true); } else { setAddingTag(true); } }}
                      clickable
                      sx={{
                        bgcolor: '#fff', color: '#64748b',
                        border: '1px dashed #cbd5e1',
                        fontWeight: 500, fontSize: '0.75rem', height: 28, borderRadius: 1.5,
                        '& .MuiChip-icon': { ml: 0.75 },
                        '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Box>

            {/* Right: lead score card */}
            <Box sx={{
              display: { xs: 'none', sm: 'block' },
              flexShrink: 0,
              minWidth: { sm: 180 },
              width: { sm: 'auto' },
              p: { sm: 1.5, md: 2 },
              borderRadius: 2,
              border: '1px solid #e2e8f0', bgcolor: '#f8fafc', textAlign: 'center',
            }}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.06em' }}>
                  LEAD SCORE
                </Typography>
                <Tooltip title="Composite fit score based on enrichment + ICP match">
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                </Tooltip>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.25 }}>
                <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, color: scoreColor, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {score ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8', fontWeight: 500 }}>/100</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 1 }}>
                Last enriched: {lastEnrichedLabel}
              </Typography>
            </Box>
          </Box>

          {/* Bottom row: meta + actions */}
          <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
            <Box sx={{
              display: { xs: 'none', sm: 'flex' },
              flex: 1, minWidth: 0,
              width: { lg: 'auto' },
              alignItems: 'center',
              gap: { sm: 1.5, md: 3.5 },
              px: { sm: 1.5, md: 2 }, py: { sm: 1, md: 1.5 },
              borderRadius: 2,
              bgcolor: '#f8fafc', border: '1px solid #f1f5f9', flexWrap: 'wrap',
            }}>
              <Box
                onClick={ownerSaving ? undefined : handleOpenOwnerMenu}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, cursor: ownerSaving ? 'wait' : 'pointer',
                  px: 0.75, py: 0.5, mx: -0.75, my: -0.5, borderRadius: 1.5,
                  '&:hover': { bgcolor: '#eef2ff' },
                }}>
                <Box sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.6875rem', color: '#94a3b8', lineHeight: 1.1, fontWeight: 500 }}>Owner:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: lead.assignedToName ? '#0f172a' : '#94a3b8', fontWeight: 600, lineHeight: 1.2, fontStyle: lead.assignedToName ? 'normal' : 'italic' }}>
                      {ownerSaving ? 'Saving…' : (lead.assignedToName || 'Unassigned')}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  </Box>
                </Box>
              </Box>
              <Menu
                anchorEl={ownerMenuAnchor}
                open={Boolean(ownerMenuAnchor)}
                onClose={() => setOwnerMenuAnchor(null)}
                slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 360 } } }}
              >
                <MenuItem
                  selected={!lead.assignedToUserId}
                  onClick={() => handleAssignOwner(null)}
                  dense
                >
                  <ListItemIcon><PersonIcon fontSize="small" sx={{ color: '#94a3b8' }} /></ListItemIcon>
                  <ListItemText primary="Unassigned" primaryTypographyProps={{ sx: { fontStyle: 'italic', color: '#64748b' } }} />
                </MenuItem>
                {allUsers.length === 0 && (
                  <MenuItem disabled dense>
                    <ListItemText primary="Loading users…" primaryTypographyProps={{ sx: { color: '#94a3b8' } }} />
                  </MenuItem>
                )}
                {allUsers.filter(u => u.isActive !== false).map(u => (
                  <MenuItem
                    key={u.id}
                    selected={u.id === lead.assignedToUserId}
                    onClick={() => handleAssignOwner(u.id)}
                    dense
                  >
                    <ListItemIcon>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 700 }}>
                        {(u.fullName || u.username || '?').charAt(0).toUpperCase()}
                      </Box>
                    </ListItemIcon>
                    <ListItemText primary={u.fullName || u.username} secondary={u.email} secondaryTypographyProps={{ sx: { fontSize: '0.6875rem' } }} />
                    {u.id === lead.assignedToUserId && <CheckIcon fontSize="small" sx={{ color: '#16a34a' }} />}
                  </MenuItem>
                ))}
              </Menu>
              <MetaItem icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />} label="Source" value={lead.source} />
              <MetaItem icon={<PublicIcon sx={{ fontSize: 18 }} />} label="Region" value={region} />
              <MetaItem icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Last activity" value={formatRelative(lead.updatedAt)} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
              <Tooltip title="Lead info">
                <IconButton
                  onClick={() => setInfoDialogOpen(true)}
                  sx={{ display: { xs: 'inline-flex', sm: 'none' }, border: '1px solid #e2e8f0', borderRadius: 1.5, width: 40, height: 40 }}>
                  <MoreHorizIcon sx={{ fontSize: 20, color: '#475569' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add note">
                <Button
                  variant="outlined" startIcon={<NoteAddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => { setQuickNoteText(''); setQuickNoteOpen(true); }}
                  sx={{ borderColor: '#e2e8f0', color: '#475569', bgcolor: '#fff', fontSize: '0.875rem', fontWeight: 600, px: { xs: 1.25, md: 1.75 }, py: 1, textTransform: 'none', borderRadius: 1.5, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Note</Box>
                </Button>
              </Tooltip>
              <Tooltip title="Add task">
                <Button
                  variant="outlined" startIcon={<PlaylistAddIcon sx={{ fontSize: 18 }} />}
                  onClick={(e) => setTaskMenuAnchor(e.currentTarget)}
                  sx={{ borderColor: '#e2e8f0', color: '#475569', bgcolor: '#fff', fontSize: '0.875rem', fontWeight: 600, px: { xs: 1.25, md: 1.75 }, py: 1, textTransform: 'none', borderRadius: 1.5, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Task</Box>
                </Button>
              </Tooltip>
              <Stack direction="row" sx={{ gap: 0 }}>
                <Tooltip title={enriching ? 'Enriching…' : 'Enrich data'}>
                  <span>
                    <Button
                      variant="contained" startIcon={enriching ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                      onClick={handleEnrich} disabled={enriching}
                      sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
                        fontSize: '0.875rem', fontWeight: 600,
                        px: { xs: 1.25, md: 2.25 }, py: 1, textTransform: 'none',
                        borderTopRightRadius: 0, borderBottomRightRadius: 0, borderTopLeftRadius: 6, borderBottomLeftRadius: 6,
                        minWidth: 0,
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                        '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                      }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{enriching ? 'Enriching…' : 'Enrich Data'}</Box>
                    </Button>
                  </span>
                </Tooltip>
                <Select
                  size="small" value={enrichAiProvider}
                  onChange={(e) => setEnrichAiProvider(e.target.value)}
                  disabled={enriching || aiProviders.length === 0}
                  renderValue={(val) => {
                    const p = aiProviders.find(pp => pp.name === val);
                    const display = p?.displayName || val || 'AI';
                    const letter = (display[0] || 'A').toUpperCase();
                    return (
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%',
                          bgcolor: '#eef2ff', color: '#4f46e5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                        }}>{letter}</Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.8rem', fontWeight: 600 }}>
                          {display}
                        </Box>
                      </Stack>
                    );
                  }}
                  sx={{
                    minWidth: { xs: 56, sm: 110 },
                    height: '100%',
                    bgcolor: '#fff',
                    borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
                    borderTopRightRadius: 6, borderBottomRightRadius: 6,
                    '& .MuiOutlinedInput-notchedOutline': { borderLeft: 0 },
                    '& .MuiSelect-select': { py: 1, pr: { xs: 3, sm: 4 } },
                  }}
                >
                  {aiProviders.length === 0 ? (
                    <MenuItem value="openai">OpenAI</MenuItem>
                  ) : (
                    aiProviders.map(p => {
                      const letter = (p.displayName?.[0] || 'A').toUpperCase();
                      return (
                        <MenuItem key={p.name} value={p.name}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{
                              width: 22, height: 22, borderRadius: '50%',
                              bgcolor: '#eef2ff', color: '#4f46e5',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.7rem', fontWeight: 700,
                            }}>{letter}</Box>
                            <Typography sx={{ fontSize: '0.8125rem' }}>{p.displayName}</Typography>
                          </Stack>
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </Stack>
              <Tooltip title="Edit lead">
                <Button
                  variant="outlined" startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                  onClick={() => navigate(`/leads/${id}/edit`)}
                  sx={{ borderColor: '#e2e8f0', color: '#0f172a', bgcolor: '#fff', fontSize: '0.875rem', fontWeight: 600, px: { xs: 1.25, md: 2 }, py: 1, textTransform: 'none', borderRadius: 1.5, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Edit</Box>
                </Button>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, width: 40, height: 40, '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
                  <MoreVertIcon sx={{ fontSize: 20, color: '#475569' }} />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={closeMore}>
                <MenuItem onClick={() => { closeMore(); handleScore(); }} disabled={scoringLead} dense>
                  <ListItemIcon><LeaderboardIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={scoringLead ? 'Scoring…' : 'Re-score lead'} />
                </MenuItem>
                {(lead.status === 'Qualified' || lead.status === 'ReadyForOutreach' || (lead.lastOutboundDate && !['Converted','Disqualified','NotInterested','NotRelevant'].includes(lead.status))) && !lead.convertedCustomerId && (
                  <MenuItem onClick={() => { closeMore(); setConvertModal(true); }} dense>
                    <ListItemIcon><CheckCircleIcon fontSize="small" sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Convert to customer" />
                  </MenuItem>
                )}
                {lead.convertedCustomerId && (
                  <MenuItem onClick={() => { closeMore(); navigate(`/customers/${lead.convertedCustomerId}`); }} dense>
                    <ListItemIcon><HandshakeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="View customer" />
                  </MenuItem>
                )}
                <Divider />
                {lead.isCompanySuppressed ? (
                  <MenuItem onClick={() => { closeMore(); handleResubscribeLead(); }} dense>
                    <ListItemIcon><MarkEmailReadIcon fontSize="small" sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Lift company suppression" secondary="Allow outreach again" />
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => { closeMore(); handleUnsubscribeLead(); }} dense>
                    <ListItemIcon><BlockIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
                    <ListItemText primary="Suppress company" secondary="Block all outreach + cancel pending" />
                  </MenuItem>
                )}
                <Divider />
                {(lead.status === 'Nurture' || lead.status === 'Archived') ? (
                  <MenuItem onClick={() => { closeMore(); handleUnpark(); }} disabled={parkBusy} dense>
                    <ListItemIcon><UnarchiveIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Unpark lead" />
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => { closeMore(); handlePark(); }} disabled={parkBusy} dense>
                    <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Park lead" />
                  </MenuItem>
                )}
                <MenuItem onClick={() => { closeMore(); handleDelete(); }} dense sx={{ color: '#dc2626' }}>
                  <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
                  <ListItemText primary="Delete lead" />
                </MenuItem>
              </Menu>
            </Stack>
            <Menu anchorEl={taskMenuAnchor} open={Boolean(taskMenuAnchor)} onClose={() => setTaskMenuAnchor(null)}>
              <MenuItem onClick={() => openTaskModal('FindContacts')} dense>
                <ListItemIcon><PersonSearchIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Find contacts" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('ProfileLead')} dense>
                <ListItemIcon><PsychologyIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Profile lead" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('SendEmail')} dense>
                <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Send email" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('LinkedInOutreach')} dense>
                <ListItemIcon><LinkedInIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Reach out on LinkedIn" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('XingOutreach')} dense>
                <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Reach out on Xing" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('Call')} dense>
                <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Call" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('Meeting')} dense>
                <ListItemIcon><VideoCallIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Schedule meeting" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('Research')} dense>
                <ListItemIcon><ManageSearchIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Research" />
              </MenuItem>
              <MenuItem onClick={() => openTaskModal('FollowUp')} dense>
                <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Follow up" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => openTaskModal('Custom')} dense>
                <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Custom task" />
              </MenuItem>
            </Menu>
          </Box>
        </Paper>
        );
      })()}

      {/* Tabs */}
      {(() => {
        const tabItems = [
          { label: 'Overview' },
          { label: 'Profile' },
          { label: `Contacts (${lead.contacts?.length || 0})` },
          { label: lead.aiInsights ? 'Insights ●' : 'Insights' },
          { label: lead.aiIcpFit ? 'ICP Profile ●' : 'ICP Profile' },
          { label: 'Activity' },
          { label: 'Outreach Timeline' },
        ];
        const switchTab = (v) => {
          setActiveTab(v);
          const next = new URLSearchParams(searchParams);
          next.set('tab', TAB_KEYS[v]);
          setSearchParams(next, { replace: true });
          if (v === 4) { fetchIcpProfile(); }
        };
        return (
          <>
            {/* Hamburger trigger (mobile) */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, mb: 2, alignItems: 'center', gap: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
              <IconButton size="small" onClick={() => setTabDrawerOpen(true)} aria-label="open sections">
                <MenuIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>
                {tabItems[activeTab]?.label}
              </Typography>
            </Box>
            <Drawer
              anchor="left"
              open={tabDrawerOpen}
              onClose={() => setTabDrawerOpen(false)}
              sx={{ display: { xs: 'block', sm: 'none' } }}
              PaperProps={{ sx: { width: 260 } }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>Sections</Typography>
              </Box>
              <List disablePadding>
                {tabItems.map((t, i) => (
                  <ListItemButton
                    key={t.label}
                    selected={activeTab === i}
                    onClick={() => { switchTab(i); setTabDrawerOpen(false); }}
                  >
                    <ListItemText primary={t.label} primaryTypographyProps={{ fontWeight: activeTab === i ? 600 : 400 }} />
                  </ListItemButton>
                ))}
              </List>
            </Drawer>
            {/* Desktop tabs */}
            <Tabs
              value={activeTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_, v) => switchTab(v)}
              sx={{ mb: 2, minHeight: 40, display: { xs: 'none', sm: 'flex' }, '& .MuiTab-root': { minHeight: 40, fontSize: '0.875rem', py: 1 } }}
            >
              {tabItems.map(t => <Tab key={t.label} label={t.label} />)}
            </Tabs>
          </>
        );
      })()}

      {/* OVERVIEW TAB */}
      {activeTab === 0 && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Website</Typography>
              <Typography>
                {lead.website
                  ? <MuiLink href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer">{lead.website}</MuiLink>
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Industry</Typography>
              <Typography>{lead.industryName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Industry Group</Typography>
              {lead.industryGroupName ? (
                <Chip
                  size="small"
                  label={lead.industryGroupName}
                  variant="outlined"
                  sx={lead.industryGroupColor ? { borderColor: lead.industryGroupColor, color: lead.industryGroupColor } : undefined}
                />
              ) : (
                <Typography>-</Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Country</Typography>
              <Typography>{lead.country || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography>{lead.phone || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography>{lead.email || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Address</Typography>
              <Typography>{lead.address || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">City / State / Postal</Typography>
              <Typography>{[lead.city, lead.state, lead.postalCode].filter(Boolean).join(', ') || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">LinkedIn</Typography>
              <Typography>
                {lead.linkedInUrl
                  ? <MuiLink href={lead.linkedInUrl.startsWith('http') ? lead.linkedInUrl : `https://${lead.linkedInUrl}`} target="_blank" rel="noreferrer">{lead.linkedInUrl}</MuiLink>
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Company Type</Typography>
              <Typography>{lead.companyType || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Headcount</Typography>
              <Typography>{lead.headcount ?? '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Source</Typography>
              <Typography>{lead.source}{lead.sourceDetail ? ` · ${lead.sourceDetail}` : ''}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Assigned To</Typography>
              <Typography>{lead.assignedToName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Tags</Typography>
              <Typography>{lead.tags || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography>{formatDate(lead.createdAt)}</Typography>
            </Grid>
          </Grid>

          {lead.description && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Description</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{lead.description}</Typography>
            </>
          )}

          {lead.notes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Internal Notes</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</Typography>
            </>
          )}

          {/* Scoring section */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Scoring</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Fit Score</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>{lead.azotteFitScore ?? '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">ICP Match</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>{lead.icpMatchPercentage != null ? `${lead.icpMatchPercentage}%` : '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Enrichment</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>{lead.enrichmentCompleteness != null ? `${lead.enrichmentCompleteness}%` : '-'}</Typography>
            </Grid>
          </Grid>

          {/* Tasks panel */}
          <Divider sx={{ my: 2 }} />
          <TasksPanel
            tasks={tasks}
            loading={tasksLoading}
            showCompleted={showCompletedTasks}
            onToggleCompleted={() => setShowCompletedTasks(v => !v)}
            onAdd={(e) => setTaskMenuAnchor(e.currentTarget)}
            onComplete={openCompleteDialog}
            onReopen={handleTaskReopen}
            onDelete={handleTaskDelete}
          />
        </Paper>
      )}

      {/* PROFILE TAB */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          {Object.keys(profileData).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>No profile data yet.</Typography>
              <Stack direction="row" sx={{ gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                <TextField
                  select size="small" label="AI Engine"
                  value={enrichAiProvider}
                  onChange={(e) => setEnrichAiProvider(e.target.value)}
                  disabled={enriching || aiProviders.length === 0}
                  sx={{ minWidth: 160 }}>
                  {aiProviders.length === 0 ? (
                    <MenuItem value="openai">OpenAI</MenuItem>
                  ) : (
                    aiProviders.map(p => (
                      <MenuItem key={p.name} value={p.name}>{p.displayName}</MenuItem>
                    ))
                  )}
                </TextField>
                <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={handleEnrich} disabled={enriching}>
                  {enriching ? 'Enriching...' : 'Run AI Enrichment'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              {Object.entries(profileData).map(([section, fields]) => (
                <Box key={section} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>{section}</Typography>
                  <Grid container spacing={1}>
                    {typeof fields === 'object' && Object.entries(fields).map(([key, val]) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                        <Typography variant="caption" color="text.secondary">{key}</Typography>
                        <Typography variant="body2">
                          {typeof val === 'object' && val !== null ? (
                            <>
                              {val.value || '-'}
                              {val.confidence && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({val.confidence}%)</Typography>}
                              {val.source && <Chip label={val.source} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.7rem', height: 18 }} />}
                            </>
                          ) : String(val)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
          </Box>
          <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            {summarySidebar}
          </Box>
        </Box>
      )}

      {/* CONTACTS TAB */}
      {activeTab === 2 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          {/* Header with search & add — always visible */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 2, gap: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupIcon sx={{ color: '#6366f1', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Contacts <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>({lead.contacts?.length || 0})</Box>
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
              {contactProviders.filter(p => p.isAvailable).length > 0 && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                  <InputLabel sx={{ fontSize: '0.8125rem' }}>AI Engines</InputLabel>
                  <Select
                    multiple
                    value={selectedContactProviders}
                    onChange={(e) => {
                      const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      setSelectedContactProviders(v);
                    }}
                    input={<OutlinedInput label="AI Engines" />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <Typography variant="caption" color="text.secondary">None</Typography>;
                      const provs = contactProviders.filter(p => selected.includes(p.name));
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {provs.map(p => (
                            <Chip key={p.name} label={p.displayName} size="small"
                              sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }} />
                          ))}
                        </Box>
                      );
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                    sx={{ '& .MuiSelect-select': { py: 0.75 } }}
                  >
                    {contactProviders.filter(p => p.isAvailable).map(p => (
                      <MenuItem key={p.name} value={p.name} dense>
                        <Checkbox size="small" checked={selectedContactProviders.includes(p.name)} />
                        <ListItemText
                          primary={p.displayName}
                          primaryTypographyProps={{ fontSize: '0.8125rem' }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                variant="outlined" size="small"
                startIcon={discovering ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 16 }} />}
                onClick={handleFindContacts} disabled={discovering}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                {discovering ? 'Searching…' : 'Find Contacts'}
              </Button>
              <Button
                variant="contained" size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/leads/${id}/contacts/new`)}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Add Contact
              </Button>
            </Stack>
          </Stack>

          {/* Discovered contacts review panel */}
          {discoveredContacts && discoveredContacts.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #bee3f8', bgcolor: '#ebf8ff' }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}><SearchIcon sx={{ fontSize: '1rem' }} /> Discovered Contacts ({discoveredContacts.length})</Typography>
                <Stack direction="row" spacing={1}>
                  {selectedDiscovered.length > 0 && (
                    <Button variant="contained" size="small" onClick={async () => {
                      const toAdd = discoveredContacts.filter((_, i) => selectedDiscovered.includes(i));
                      for (const c of toAdd) { await handleAddDiscoveredContact(c); }
                      setSelectedDiscovered([]);
                    }}>Add {selectedDiscovered.length} Selected</Button>
                  )}
                  <Button variant="contained" size="small" onClick={async () => {
                    const all = [...discoveredContacts];
                    for (const c of all) { await handleAddDiscoveredContact(c); }
                    setSelectedDiscovered([]);
                  }}>Add All</Button>
                  <Button variant="outlined" size="small" onClick={() => { setDiscoveredContacts(null); setSelectedDiscovered([]); }}>Dismiss</Button>
                </Stack>
              </Stack>
              <AzotteGrid
                columns={discoveredColumns}
                rows={discoveredContacts}
                totalCount={discoveredContacts.length}
                loading={false}
                actionColumnKey="actions"
              />
            </Paper>
          )}
          {discoveredContacts && discoveredContacts.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No contacts discovered. Try enriching the lead's website first for better results.
            </Alert>
          )}

          {(!lead.contacts || lead.contacts.length === 0) ? (
            <Box sx={{
              textAlign: 'center', py: { xs: 4, md: 6 }, px: 2,
              border: '1px dashed #e2e8f0', borderRadius: 2,
              bgcolor: '#fafbfc',
            }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
              }}>
                <PersonSearchIcon sx={{ fontSize: 28, color: '#6366f1' }} />
              </Box>
              <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>No contacts yet</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Discover contacts via AI engines or add one manually.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={discovering ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 16 }} />}
                  onClick={handleFindContacts} disabled={discovering}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  {discovering ? 'Searching…' : 'Find Contacts'}
                </Button>
                <Button
                  variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate(`/leads/${id}/contacts/new`)}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  Add First Contact
                </Button>
              </Stack>
            </Box>
          ) : (
            <AzotteGrid
              columns={contactColumns}
              rows={sortedContacts.slice(contactsPage * contactsRowsPerPage, contactsPage * contactsRowsPerPage + contactsRowsPerPage)}
              totalCount={sortedContacts.length}
              page={contactsPage}
              setPage={setContactsPage}
              rowsPerPage={contactsRowsPerPage}
              handleRowsPerPageChange={(e) => { setContactsRowsPerPage(parseInt(e.target.value, 10)); setContactsPage(0); }}
              loading={false}
              actionColumnKey="actions"
            />
          )}
        </Paper>
          </Box>
        </Box>
      )}

      {/* INSIGHTS TAB */}
      {activeTab === 3 && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: '#6366f1' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Strategic Brief</Typography>
            {lead.aiIcpFit && (
              <Chip
                size="small"
                label={`ICP: ${lead.aiIcpFit === 'CurrentFit' ? 'Current Fit' : lead.aiIcpFit === 'FuturePotential' ? 'Future Potential' : lead.aiIcpFit === 'NotAFit' ? 'Not a Fit' : 'Unknown'}`}
                color={lead.aiIcpFit === 'CurrentFit' ? 'success' : lead.aiIcpFit === 'FuturePotential' ? 'info' : lead.aiIcpFit === 'NotAFit' ? 'error' : 'default'}
                variant="filled"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {lead.aiInsightsProvider ? `· ${lead.aiInsightsProvider}` : ''}
              {lead.aiInsightsUpdatedAt ? ` · ${formatRelative(lead.aiInsightsUpdatedAt)}` : ''}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {!insightsEditing && lead.aiInsights && (
              <Button size="small" variant="text" startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => { setInsightsDraft(lead.aiInsights || ''); setInsightsEditing(true); }}
                sx={{ textTransform: 'none' }}>
                Edit
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              onClick={handleEnrich} disabled={enriching || insightsEditing}
              sx={{ textTransform: 'none' }}>
              Regenerate
            </Button>
          </Stack>

          {!lead.aiInsights && !insightsEditing && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography sx={{ mb: 1 }}>No AI insights yet.</Typography>
              <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleEnrich} disabled={enriching}>
                Enrich Data to generate
              </Button>
            </Box>
          )}

          {!insightsEditing && lead.aiInsights && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafbff', borderColor: '#e0e7ff' }}>
              <Box>{renderInsightsMarkdown(lead.aiInsights)}</Box>
            </Paper>
          )}

          {insightsEditing && (
            <>
              <TextField
                fullWidth multiline minRows={14} value={insightsDraft}
                onChange={(e) => setInsightsDraft(e.target.value)}
                placeholder="## Additional Context&#10;...&#10;## ICP Fit&#10;...&#10;ICP-VERDICT: CurrentFit&#10;## Signals to Track&#10;- ...&#10;## Outreach Recommendation&#10;- ..."
                sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="contained" disabled={insightsSaving}
                  onClick={async () => {
                    setInsightsSaving(true);
                    try {
                      await updateLeadAiInsights(id, insightsDraft);
                      setInsightsEditing(false);
                      fetchLead();
                    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
                    finally { setInsightsSaving(false); }
                  }}>
                  {insightsSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button variant="outlined" onClick={() => setInsightsEditing(false)} disabled={insightsSaving}>Cancel</Button>
              </Stack>
            </>
          )}
        </Paper>
      )}

      {/* ICP PROFILE TAB — extracted ICP fit values + watch signals + segments */}
      {activeTab === 4 && (
        <IcpProfilePanel
          lead={lead}
          icpData={icpData}
          icpLoading={icpLoading}
          onRegenerate={handleEnrich}
          enriching={enriching}
          onRefresh={fetchIcpProfile}
          onUpdateSignalStatus={async (signalId, status) => {
            await updateLeadSignalStatus(id, signalId, status);
            fetchIcpProfile();
          }}
          onDeleteSignal={async (signalId) => {
            await deleteLeadSignal(id, signalId);
            fetchIcpProfile();
          }}
          onRemoveSegment={async (memberId) => {
            await removeLeadSegmentationMember(id, memberId);
            fetchIcpProfile();
          }}
        />
      )}

      {/* TIMELINE TAB */}
      {activeTab === 5 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {/* Next Outreach Hint */}
        <Paper
          variant="outlined"
          sx={{
            mb: 2, borderRadius: 2, overflow: 'hidden',
            border: '1px solid #fed7aa',
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          }}
        >
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ px: 2, py: 1.25, alignItems: { sm: 'center' }, gap: 1, borderBottom: '1px solid #fed7aa', bgcolor: 'rgba(255,255,255,0.4)' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: '#fff', border: '1px solid #fed7aa',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <LightbulbIcon sx={{ color: '#ea580c', fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: '#9a3412', letterSpacing: 0.3, lineHeight: 1.2 }}>
                  NEXT OUTREACH HINT
                </Typography>
                {(lead.nextOutreachHintUpdatedAt || lead.nextOutreachHintProvider) && (
                  <Typography variant="caption" sx={{ color: '#9a3412', opacity: 0.75 }}>
                    {lead.nextOutreachHintUpdatedAt && formatRelative(lead.nextOutreachHintUpdatedAt)}
                    {lead.nextOutreachHintProvider && ` · ${lead.nextOutreachHintProvider}`}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Button
              size="small" variant="outlined" disabled={outreachHintLoading}
              startIcon={outreachHintLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={handleRefreshOutreachHint}
              sx={{
                textTransform: 'none', flexShrink: 0, borderRadius: 1.5,
                bgcolor: '#fff', borderColor: '#fed7aa', color: '#9a3412', fontWeight: 600,
                '&:hover': { borderColor: '#fdba74', bgcolor: '#fff7ed' },
              }}
            >
              {outreachHintLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>

          {/* Body */}
          <Box sx={{ p: 2 }}>
            {lead.nextOutreachHint ? (
              <OutreachHintBody text={lead.nextOutreachHint} />
            ) : (
              <Typography variant="body2" sx={{ color: '#9a3412', fontStyle: 'italic', opacity: 0.85 }}>
                No hint yet — click Refresh to generate based on the latest activity.
              </Typography>
            )}
          </Box>
        </Paper>
        <Box>
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, mb: 2, gap: 1.5 }} spacing={0}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>Multi-Channel Timeline</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={timelineFilter}
                onChange={(_, v) => { if (v) { setTimelineFilter(v); fetchTimeline(v); } }}
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontSize: '0.75rem', px: 1.25, py: 0.5,
                    border: '1px solid #e2e8f0', color: '#475569', gap: 0.5,
                    '&.Mui-selected': { bgcolor: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe', fontWeight: 600 },
                    '&.Mui-selected:hover': { bgcolor: '#e0e7ff' },
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="Email"><EmailIcon sx={{ fontSize: '0.9rem' }} /> Email</ToggleButton>
                <ToggleButton value="LinkedInManual"><LinkedInIcon sx={{ fontSize: '0.9rem' }} /> LinkedIn</ToggleButton>
                <ToggleButton value="Call"><PhoneIcon sx={{ fontSize: '0.9rem' }} /> Call</ToggleButton>
                <ToggleButton value="Note"><NotesIcon sx={{ fontSize: '0.9rem' }} /> Note</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained" size="small"
                startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/email-compose/${id}`)}
                sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
              >
                Compose Email
              </Button>
              <Button
                variant="outlined" size="small"
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => setActivityActionsAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', borderRadius: 1.5, borderColor: '#e2e8f0', color: '#475569' }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={activityActionsAnchor}
                open={Boolean(activityActionsAnchor)}
                onClose={() => setActivityActionsAnchor(null)}
              >
                <MenuItem onClick={() => { setActivityActionsAnchor(null); openMeetingModal(); }} dense>
                  <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Schedule meeting" />
                </MenuItem>
                <MenuItem onClick={() => { setActivityActionsAnchor(null); setLinkedInPanel(!linkedInPanel); }} dense>
                  <ListItemIcon><LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} /></ListItemIcon>
                  <ListItemText primary={linkedInPanel ? 'Hide LinkedIn assist' : 'LinkedIn assist'} />
                </MenuItem>
                <MenuItem onClick={() => { setActivityActionsAnchor(null); setInboundModal(true); }} dense>
                  <ListItemIcon><MoveToInboxIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Log inbound" />
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>

          {/* LinkedIn Assist Panel */}
          {linkedInPanel && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #bee3f8', bgcolor: '#ebf8ff' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 , display: 'flex', alignItems: 'center', gap: 0.5 }}><LinkedInIcon sx={{ color: '#0077b5' }} /> LinkedIn Manual Assist</Typography>

              {/* Sender chip */}
              <Box sx={{ p: 1, mb: 1.5, bgcolor: '#fff', border: '1px solid #cbd5e0', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Sender</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {currentUser?.fullName || 'Loading...'}
                  {currentUser?.jobTitle && ` — ${currentUser.jobTitle}`}
                </Typography>
                {(!currentUser?.fullName || !currentUser?.jobTitle) && (
                  <Typography variant="caption" color="warning.main">
                    Set Full Name + Job Title in Management → Users to fill SenderName/SenderTitle.
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select size="small" fullWidth label="Contact" value={linkedInContact} onChange={e => setLinkedInContact(e.target.value)}>
                    <MenuItem value="">— Select —</MenuItem>
                    {(lead.contacts || []).map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select size="small" fullWidth label="Stage" value={linkedInStage} onChange={e => setLinkedInStage(e.target.value)}>
                    {['FirstContact', 'FollowUp', 'Qualification', 'ReEngagement'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select size="small" fullWidth label="LLM Provider"
                    value={linkedInAiProvider}
                    onChange={e => setLinkedInAiProvider(e.target.value)}>
                    {aiProviders.length === 0 ? (
                      <MenuItem value="openai">OpenAI (default)</MenuItem>
                    ) : (
                      aiProviders.map(p => (
                        <MenuItem key={p.name} value={p.name}>
                          {p.displayName}{p.isDefault ? ' (default)' : ''}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField select size="small" fullWidth
                    label={`LinkedIn template (${linkedInTemplates.length})`}
                    value={linkedInTemplateId}
                    onChange={e => setLinkedInTemplateId(e.target.value)}
                    helperText={linkedInTemplates.length === 0 ? 'No LinkedIn templates for this stage. Create one in Management.' : 'Selected template body is sent as base context to the AI.'}
                  >
                    <MenuItem value="">— No template (free generation) —</MenuItem>
                    {linkedInTemplates.map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                        {t.matchScore != null && t.matchScore >= 10 && ' ★'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Opening picker (when template has openings) */}
                {(() => {
                  const tpl = linkedInTemplates.find(t => String(t.id) === String(linkedInTemplateId));
                  let openings = [];
                  if (tpl?.openings) {
                    try { const p = JSON.parse(tpl.openings); if (Array.isArray(p)) openings = p; } catch {}
                  }
                  if (openings.length === 0) return null;
                  return (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField select size="small" fullWidth label="Opening (by tone)"
                        value={linkedInOpeningTone}
                        onChange={e => setLinkedInOpeningTone(e.target.value)}>
                        <MenuItem value="">— No opening —</MenuItem>
                        {openings.map((o, i) => (
                          <MenuItem key={i} value={o.tone}>
                            {o.tone}: {(o.text || '').slice(0, 50)}{(o.text || '').length > 50 ? '...' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  );
                })()}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select size="small" fullWidth label="Improvement prompt (optional)"
                    value={linkedInImprovePromptId}
                    onChange={e => setLinkedInImprovePromptId(e.target.value)}
                    helperText={linkedInImprovePrompts.length === 0 ? 'No prompts. Add in Management → AI Prompts.' : 'Style guidance applied during generation'}
                  >
                    <MenuItem value="">— Default —</MenuItem>
                    {linkedInImprovePrompts.map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        [{p._category}] {p.name}{p.isDefault ? ' ★' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                <Button variant="contained" size="small" startIcon={<AutoFixHighIcon />} onClick={handleGenerateLinkedIn} disabled={linkedInGenerating}>
                  {linkedInGenerating ? 'Generating...' : 'Generate Message'}
                </Button>
                {linkedInMsg && (
                  <Button variant="outlined" size="small" color="secondary" startIcon={<AutoFixHighIcon />}
                    onClick={handlePersonalizeLinkedIn} disabled={linkedInPersonalizing}>
                    {linkedInPersonalizing ? 'Personalizing...' : 'Personalize for Lead'}
                  </Button>
                )}
              </Stack>
              {linkedInMsg && (
                <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff' }}>
                  {linkedInMsg.subject && <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{linkedInMsg.subject}</Typography>}
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{linkedInMsg.message}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard.writeText(linkedInMsg.message)}>
                      Copy to Clipboard
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color={linkedInLogged ? 'success' : 'primary'}
                      onClick={handleLogSentLinkedIn}
                      disabled={linkedInLogging}
                    >
                      {linkedInLogging ? 'Logging...' : linkedInLogged ? '✓ Logged to Activities' : 'Mark as Sent'}
                    </Button>
                  </Stack>
                </Paper>
              )}

              {linkedInPersonalizePreview && (
                <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: '#fffbea', border: '1px solid #f6e05e' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Personalized version:</Typography>
                  {linkedInPersonalizePreview.changesApplied?.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mb: 1, pl: 2 }}>
                      {linkedInPersonalizePreview.changesApplied.map((c, i) => (
                        <Typography component="li" key={i} variant="caption" color="text.secondary">{c}</Typography>
                      ))}
                    </Box>
                  )}
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', mb: 1 }}>
                    {linkedInPersonalizePreview.personalizedBody}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" size="small" onClick={handleApplyLinkedInPersonalize}>Apply</Button>
                    <Button variant="outlined" size="small" onClick={() => setLinkedInPersonalizePreview(null)}>Dismiss</Button>
                  </Stack>
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Paste LinkedIn Reply for Analysis</Typography>
              <TextField size="small" fullWidth multiline rows={3} value={linkedInReply} onChange={e => setLinkedInReply(e.target.value)} placeholder="Paste the lead's LinkedIn reply here..." sx={{ mt: 0.5 }} />
              <Button variant="outlined" size="small" sx={{ mt: 0.5 }} onClick={handleAnalyzeLinkedInReply} disabled={linkedInAnalyzing}>
                {linkedInAnalyzing ? 'Analyzing...' : 'Analyze Reply'}
              </Button>
              {linkedInAnalysis && (
                <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: '#fff' }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                    <Chip label={linkedInAnalysis.intent || 'Unknown'} color="primary" size="small" />
                    {linkedInAnalysis.sentiment && <Chip label={linkedInAnalysis.sentiment} size="small" variant="outlined" />}
                  </Stack>
                  {linkedInAnalysis.summary && <Typography variant="body2"><strong>Summary:</strong> {linkedInAnalysis.summary}</Typography>}
                  {linkedInAnalysis.suggestedNextStep && <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Suggested Next:</strong> {linkedInAnalysis.suggestedNextStep}</Typography>}
                </Paper>
              )}
            </Paper>
          )}

          {/* Marketing-site visits (analytics tracker) */}
          {marketingEvents.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #c7d2fe', bgcolor: '#eef2ff' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ManageSearchIcon /> Marketing Activity
                <Chip size="small" label={marketingEvents.length} sx={{ ml: 1, height: 20 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Last seen {new Date(marketingEvents[0].receivedAt).toLocaleString()}
                {marketingEvents[0].utmCampaign && <> · campaign <strong>{marketingEvents[0].utmCampaign}</strong></>}
              </Typography>
              <Stack spacing={0.5}>
                {marketingEvents.slice(0, 8).map(ev => (
                  <Box key={ev.id} sx={{
                    display: 'flex', gap: 1, alignItems: 'center',
                    p: '6px 10px', bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e7ff',
                  }}>
                    <Chip size="small" label={ev.event}
                      color={ev.event === 'cta_click' ? 'primary' : 'default'}
                      sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.page}>
                      {ev.page || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                      {new Date(ev.receivedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
                {marketingEvents.length > 8 && (
                  <Typography variant="caption" color="text.secondary">+ {marketingEvents.length - 8} more</Typography>
                )}
              </Stack>
            </Paper>
          )}

          {/* Follow-Ups Section */}
          {followUps.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #fbd38d', bgcolor: '#fffaf0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 , display: 'flex', alignItems: 'center', gap: 0.5 }}><AssignmentIcon /> Follow-Ups ({followUps.filter(f => !f.isFollowUpCompleted).length} pending)</Typography>
              <Stack spacing={1}>
                {followUps.map(fu => (
                  <Box key={fu.interactionId} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    p: '8px 12px', borderRadius: 1.5,
                    bgcolor: fu.isFollowUpCompleted ? '#f0fff4' : '#fff',
                    border: `1px solid ${fu.isFollowUpCompleted ? '#c6f6d5' : new Date(fu.followUpDate) < new Date() ? '#fed7d7' : '#e2e8f0'}`,
                    opacity: fu.isFollowUpCompleted ? 0.6 : 1,
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {fu.isFollowUpCompleted
                          ? <CheckCircleIcon fontSize="small" sx={{ color: '#38a169', flexShrink: 0 }} />
                          : new Date(fu.followUpDate) < new Date()
                            ? <ScheduleIcon fontSize="small" sx={{ color: '#e53e3e', flexShrink: 0 }} />
                            : <NotificationsNoneIcon fontSize="small" sx={{ color: '#dd6b20', flexShrink: 0 }} />}
                        {fu.followUpAction || fu.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Due: {new Date(fu.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {fu.outcome && <span> · Outcome: {fu.outcome}</span>}
                      </Typography>
                    </Box>
                    {!fu.isFollowUpCompleted && (
                      <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={() => handleCompleteFollowUp(fu.interactionId)}>
                        Complete
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Meetings Section */}
          {meetings.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #bee3f8', bgcolor: '#ebf8ff' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 , display: 'flex', alignItems: 'center', gap: 0.5 }}><CalendarMonthIcon /> Scheduled Meetings ({meetings.filter(m => new Date(m.startTime) >= new Date()).length} upcoming)</Typography>
              <Stack spacing={1}>
                {meetings.map(m => {
                  const isPast = new Date(m.endTime) < new Date();
                  return (
                    <Box key={m.id} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      p: '8px 12px', borderRadius: 1.5,
                      bgcolor: isPast ? '#f7fafc' : '#fff',
                      border: `1px solid ${isPast ? '#e2e8f0' : '#bee3f8'}`,
                      opacity: isPast ? 0.7 : 1,
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isPast ? <EventBusyIcon fontSize="small" sx={{ color: '#a0aec0', flexShrink: 0 }} /> : <CalendarMonthIcon fontSize="small" sx={{ color: '#3182ce', flexShrink: 0 }} />} {m.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(m.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{' '}
                          {new Date(m.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} –{' '}
                          {new Date(m.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          {m.location && <span> · <LocationOnIcon sx={{ fontSize: '0.75rem', verticalAlign: 'middle' }} /> {m.location}</span>}
                        </Typography>
                        {m.attendees?.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <GroupIcon sx={{ fontSize: '0.75rem' }} /> {m.attendees.map(a => a.fullName).join(', ')}
                          </Typography>
                        )}
                        {m.leadContactName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Contact: {m.leadContactName}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" variant="outlined" component="a" href={generateIcsUrl(m)} download={`meeting-${m.id}.ics`} startIcon={<DownloadIcon />} sx={{ fontSize: '0.7rem', textDecoration: 'none' }}>
                          .ics
                        </Button>
                        {!isPast && (
                          <Button size="small" variant="outlined" onClick={() => handleCancelMeeting(m.id)} sx={{ fontSize: '0.7rem' }}>Cancel</Button>
                        )}
                        {isPast && (
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteMeeting(m.id)} sx={{ fontSize: '0.7rem' }}></Button>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* Quick Note */}
          <Paper variant="outlined" sx={{ p: 1, mb: 2, display: 'flex', gap: 1, alignItems: 'center', borderRadius: 1.5, bgcolor: '#fafbfc' }}>
            <NotesIcon sx={{ color: '#94a3b8', fontSize: 18, ml: 0.5 }} />
            <TextField
              size="small" fullWidth variant="standard"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a quick note…"
              onKeyDown={(e) => { if (e.key === 'Enter' && noteText.trim()) { e.preventDefault(); handleAddNote(); } }}
              InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' } }}
            />
            <Button
              variant="contained" size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddNote}
              disabled={!noteText.trim()}
              sx={{ textTransform: 'none', borderRadius: 1.5, flexShrink: 0 }}
            >
              Add
            </Button>
          </Paper>

          {/* Timeline List */}
          {timelineLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /><Typography color="text.secondary" sx={{ mt: 1 }}>Loading timeline...</Typography></Box>
          ) : timeline.length === 0 ? (
            <Box sx={{
              textAlign: 'center', py: { xs: 4, md: 6 }, px: 2,
              border: '1px dashed #e2e8f0', borderRadius: 2, bgcolor: '#fafbfc',
            }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
              }}>
                <ScheduleIcon sx={{ fontSize: 28, color: '#6366f1' }} />
              </Box>
              <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>No interactions yet</Typography>
              <Typography variant="caption" color="text.secondary">
                Compose an email, schedule a meeting, or add a quick note to start tracking.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {timeline.map((item, idx) => (
                <Paper key={item.id || idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={{ minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {channelIcon(item.channel)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{item.subject || item.type || 'Note'}</Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={item.direction || 'Note'} size="small"
                              sx={{ fontSize: '0.75rem', bgcolor: directionColor(item.direction) + '20', color: directionColor(item.direction) }} />
                            {item.status && (
                              <Chip label={item.status} size="small"
                                sx={{ fontSize: '0.75rem', bgcolor: statusColor(item.status) + '20', color: statusColor(item.status) }} />
                            )}
                            {item.stage && <Chip label={item.stage} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                            {item.channel && <Chip label={item.channel} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                            {item.source && (
                              <Chip label={item.source} size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  bgcolor: item.source.startsWith('Campaign') ? '#805ad520' : '#71809620',
                                  color: item.source.startsWith('Campaign') ? '#805ad5' : '#718096',
                                }} />
                            )}
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                          {item.status === 'Scheduled' && item.id > 0 && (
                            <Tooltip title="Remove this scheduled email from the queue">
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleCancelScheduledEmail(item.id)}
                                sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}
                              >
                                Cancel
                              </Button>
                            </Tooltip>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {item.interactionDate ? new Date(item.interactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </Typography>
                        </Stack>
                      </Stack>
                      {item.contactPersonName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Contact: {item.contactPersonName}
                        </Typography>
                      )}
                      {item.notes && (
                        <Typography variant="body2" sx={{ color: '#4a5568', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {item.notes.length > 200 ? item.notes.substring(0, 200) + '...' : item.notes}
                        </Typography>
                      )}
                      {item.body && (
                        <TimelineBodyView body={item.body} channel={item.channel} />
                      )}
                      {item.aiSummary && (
                        <Box sx={{ fontSize: '0.8rem', mt: 0.5, p: '4px 8px', bgcolor: '#f0fff4', borderRadius: 1, color: '#276749' }}>
                          <AutoAwesomeIcon sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'middle' }} /> AI: {item.aiSummary}
                        </Box>
                      )}
                      {item.aiSuggestedNextStep && (
                        <Box sx={{ fontSize: '0.8rem', mt: 0.25, p: '4px 8px', bgcolor: '#ebf8ff', borderRadius: 1, color: '#2b6cb0' }}>
                          → Next: {item.aiSuggestedNextStep}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
          </Box>
        </Box>
      )}

      {/* OUTREACH TIMELINE TAB */}
      {activeTab === 6 && (
        <OutreachTimelineTab leadId={id} />
      )}

      {/* INBOUND EMAIL MODAL */}
      {inboundModal && (
        <Modal title="Log Inbound Email" onClose={() => { setInboundModal(false); setInboundResult(null); }}>
          {inboundResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 1.5 }}>Email processed successfully</Alert>
              {(inboundResult.aiIntent || inboundResult.intent) && (
                <Typography><strong>Intent:</strong> {inboundResult.aiIntent || inboundResult.intent}</Typography>
              )}
              {(inboundResult.aiSummary || inboundResult.summary) && (
                <Typography sx={{ mt: 0.5 }}><strong>Summary:</strong> {inboundResult.aiSummary || inboundResult.summary}</Typography>
              )}
              {(inboundResult.aiSuggestedNextStep || inboundResult.suggestedNextStep) && (
                <Typography sx={{ mt: 0.5 }}><strong>Suggested Next:</strong> {inboundResult.aiSuggestedNextStep || inboundResult.suggestedNextStep}</Typography>
              )}
              {(inboundResult.cancelledPendingCount > 0
                || inboundResult.closedFollowUpCount > 0
                || inboundResult.leadStatusChanged
                || inboundResult.markedPrimaryContactId) && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#22543d' }}>Side effects applied</Typography>
                  <Stack spacing={0.25}>
                    {inboundResult.leadStatusChanged && (
                      <Typography variant="caption">• Lead status moved to <strong>Responded</strong></Typography>
                    )}
                    {inboundResult.markedPrimaryContactId && (
                      <Typography variant="caption">• Replying contact marked as primary (in-contact person)</Typography>
                    )}
                    {inboundResult.cancelledPendingCount > 0 && (
                      <Typography variant="caption">• Cancelled <strong>{inboundResult.cancelledPendingCount}</strong> pending email{inboundResult.cancelledPendingCount === 1 ? '' : 's'}</Typography>
                    )}
                    {inboundResult.closedFollowUpCount > 0 && (
                      <Typography variant="caption">• Closed <strong>{inboundResult.closedFollowUpCount}</strong> open follow-up{inboundResult.closedFollowUpCount === 1 ? '' : 's'}</Typography>
                    )}
                  </Stack>
                </Box>
              )}
              <DialogActions sx={{ mt: 1.5, px: 0 }}>
                <Button variant="contained" onClick={() => { setInboundModal(false); setInboundResult(null); }}>Close</Button>
              </DialogActions>
            </Box>
          ) : (
            <form onSubmit={e => { e.preventDefault(); handleInboundSubmit(); }}>
              <Stack spacing={2}>
                <TextField size="small" fullWidth required type="email" label="From Email" value={inboundForm.fromEmail} onChange={e => setInboundForm({ ...inboundForm, fromEmail: e.target.value })} helperText="Used to auto-match the replying contact when not picked below." />
                <TextField select size="small" fullWidth label="Replying contact (optional)" value={inboundForm.contactId} onChange={e => setInboundForm({ ...inboundForm, contactId: e.target.value })} helperText="Pick to override email match. Replier becomes primary; pending emails cancel; follow-ups close.">
                  <MenuItem value="">— Auto-match from email —</MenuItem>
                  {(lead.contacts || []).map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.firstName} {c.lastName}{c.email ? ` <${c.email}>` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField size="small" fullWidth label="Subject" value={inboundForm.subject} onChange={e => setInboundForm({ ...inboundForm, subject: e.target.value })} />
                <TextField size="small" fullWidth required multiline rows={6} label="Body" value={inboundForm.body} onChange={e => setInboundForm({ ...inboundForm, body: e.target.value })} />
              </Stack>
              <DialogActions sx={{ mt: 2, px: 0 }}>
                <Button variant="outlined" onClick={() => setInboundModal(false)}>Cancel</Button>
                <Button variant="contained" type="submit" disabled={inboundProcessing}>
                  {inboundProcessing ? 'Processing...' : 'Process & Classify'}
                </Button>
              </DialogActions>
            </form>
          )}
        </Modal>
      )}

      {/* INTERACTION MODAL */}
      {interactionModal && (
        <Modal title={interactionModal === 'add' ? 'Add Interaction' : 'Edit Interaction'} onClose={() => setInteractionModal(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            try {
              const payload = {
                ...interactionForm,
                contactPersonId: interactionForm.contactPersonId || null,
                outcome: interactionForm.outcome || null,
                followUpDate: interactionForm.followUpDate || null,
              };
              if (interactionModal === 'add') await createLeadInteraction(id, payload);
              else await updateLeadInteraction(id, interactionModal.id, payload);
              setInteractionModal(null);
              fetchTimeline();
            } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
          }}>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select size="small" fullWidth label="Type *" value={interactionForm.type} onChange={e => setInteractionForm({ ...interactionForm, type: e.target.value })}>
                    {['Email', 'PhoneCall', 'Meeting', 'VideoCall', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth type="date" label="Date *" required value={interactionForm.interactionDate || ''} onChange={e => setInteractionForm({ ...interactionForm, interactionDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
              </Grid>
              <TextField size="small" fullWidth required label="Subject" value={interactionForm.subject || ''} onChange={e => setInteractionForm({ ...interactionForm, subject: e.target.value })} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select size="small" fullWidth label="Contact" value={interactionForm.contactPersonId || ''} onChange={e => setInteractionForm({ ...interactionForm, contactPersonId: e.target.value ? parseInt(e.target.value) : null })}>
                    <MenuItem value="">— Select —</MenuItem>
                    {(lead.contacts || []).map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth label="Conducted By" value={interactionForm.conductedBy || ''} onChange={e => setInteractionForm({ ...interactionForm, conductedBy: e.target.value })} />
                </Grid>
              </Grid>
              <TextField size="small" fullWidth multiline rows={3} label="Notes" value={interactionForm.notes || ''} onChange={e => setInteractionForm({ ...interactionForm, notes: e.target.value })} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select size="small" fullWidth label="Outcome" value={interactionForm.outcome || ''} onChange={e => setInteractionForm({ ...interactionForm, outcome: e.target.value })}>
                    <MenuItem value="">— Select —</MenuItem>
                    {['Positive', 'Neutral', 'Negative', 'NoResponse', 'FollowUpNeeded'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth type="date" label="Follow-Up Date" value={interactionForm.followUpDate || ''} onChange={e => setInteractionForm({ ...interactionForm, followUpDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
              </Grid>
              <TextField size="small" fullWidth label="Follow-Up Action" value={interactionForm.followUpAction || ''} onChange={e => setInteractionForm({ ...interactionForm, followUpAction: e.target.value })} />
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setInteractionModal(null)}>Cancel</Button>
              <Button variant="contained" type="submit">{interactionModal === 'add' ? 'Add' : 'Save'}</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* MEETING MODAL */}
      {meetingModal && (
        <Modal title="Schedule Meeting" onClose={() => setMeetingModal(false)}>
          <form onSubmit={handleScheduleMeeting}>
            <Stack spacing={2}>
              <TextField size="small" fullWidth required label="Title" value={meetingForm.title} onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth type="datetime-local" label="Start *" required value={meetingForm.startTime} onChange={e => setMeetingForm({ ...meetingForm, startTime: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth type="datetime-local" label="End *" required value={meetingForm.endTime} onChange={e => setMeetingForm({ ...meetingForm, endTime: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField size="small" fullWidth label="Location" value={meetingForm.location} onChange={e => setMeetingForm({ ...meetingForm, location: e.target.value })} placeholder="Office, Zoom link, etc." />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select size="small" fullWidth label="Contact" value={meetingForm.leadContactId} onChange={e => setMeetingForm({ ...meetingForm, leadContactId: e.target.value })}>
                    <MenuItem value="">— Select —</MenuItem>
                    {(lead.contacts || []).map(c => <MenuItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <TextField size="small" fullWidth multiline rows={2} label="Description" value={meetingForm.description} onChange={e => setMeetingForm({ ...meetingForm, description: e.target.value })} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Invite Teammates</Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {allUsers.filter(u => u.isActive).map(u => (
                    <FormControlLabel
                      key={u.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={meetingForm.attendeeUserIds.includes(u.id)}
                          onChange={e => {
                            const ids = e.target.checked
                              ? [...meetingForm.attendeeUserIds, u.id]
                              : meetingForm.attendeeUserIds.filter(uid => uid !== u.id);
                            setMeetingForm({ ...meetingForm, attendeeUserIds: ids });
                          }}
                        />
                      }
                      label={u.fullName || u.username}
                      sx={{
                        mr: 0, px: 1, borderRadius: 1,
                        bgcolor: meetingForm.attendeeUserIds.includes(u.id) ? '#ebf8ff' : '#f7fafc',
                        border: '1px solid', borderColor: 'divider',
                        '& .MuiFormControlLabel-label': { fontSize: '0.85rem' },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
              <TextField size="small" fullWidth multiline rows={2} label="Notes" value={meetingForm.notes} onChange={e => setMeetingForm({ ...meetingForm, notes: e.target.value })} />
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setMeetingModal(false)}>Cancel</Button>
              <Button variant="contained" type="submit">Schedule</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* ENRICHMENT PROGRESS MODAL */}
      {enriching && (
        <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2, color: '#6366f1' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
              Enriching lead with AI…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Researching the company, gathering missing fields, and generating a strategic brief. This usually takes 10–40 seconds.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button variant="outlined" color="error" onClick={cancelEnrich}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ENRICHMENT PREVIEW MODAL — suggestions auto-applied; this is read-only preview with 2 tabs */}
      {reviewData && (
        <Modal title="AI Enrichment Result" onClose={closeReviewModal}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Completeness: {reviewData.completenessBefore}%
            {reviewData.providerUsed && <> · Source: {reviewData.providerUsed}</>}
            {reviewData.suggestions?.length > 0 && (
              <> · <Box component="span" sx={{ color: '#16a34a', fontWeight: 600 }}>
                {reviewData.suggestions.length} field{reviewData.suggestions.length === 1 ? '' : 's'} auto-applied
              </Box></>
            )}
          </Typography>
          {autoApplyError && (
            <Typography variant="body2" sx={{ mb: 1, color: '#b91c1c' }}>{autoApplyError}</Typography>
          )}

          <Tabs
            value={reviewModalTab}
            onChange={(_, v) => setReviewModalTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 40 }}
          >
            <Tab label="AI Insights" sx={{ minHeight: 40, textTransform: 'none' }} />
            <Tab
              label={`Applied Fields${reviewData.suggestions?.length ? ` (${reviewData.suggestions.length})` : ''}`}
              sx={{ minHeight: 40, textTransform: 'none' }}
            />
          </Tabs>

          {reviewModalTab === 0 && (
            reviewData.insights?.markdown ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafbff', borderColor: '#e0e7ff' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                    AI INSIGHTS{reviewData.insights.providerUsed ? ` · ${reviewData.insights.providerUsed}` : ''}
                  </Typography>
                </Stack>
                <Box>{renderInsightsMarkdown(reviewData.insights.markdown)}</Box>
              </Paper>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary">No insights returned for this lead.</Typography>
              </Box>
            )
          )}

          {reviewModalTab === 1 && (
            (!reviewData.suggestions || reviewData.suggestions.length === 0) ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary">
                  No field suggestions returned. The lead may already be fully enriched, or the AI provider returned no data.
                </Typography>
              </Box>
            ) : (
              <Box>
                {reviewData.suggestions.map((s, idx) => {
                  const decision = enrichmentDecisions[s.fieldName];
                  return (
                    <Paper key={idx} variant="outlined" sx={{
                      p: 1.5, mb: 1,
                      bgcolor: decision === 'accepted' ? '#f0fdf4' : '#f7fafc',
                    }}>
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{enrichmentFieldLabel(s.fieldName)}</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <Typography component="span" color="text.secondary">Previous: </Typography>
                            {s.currentValue || <em style={{ color: '#a0aec0' }}>empty</em>}
                          </Typography>
                          <Typography variant="body2">
                            <Typography component="span" color="text.secondary">Applied: </Typography>
                            <strong>{s.suggestedValue}</strong>
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: confColor(s.confidence), fontWeight: 600 }}>
                              {Math.round(s.confidence * 100)}% confident
                            </Typography>
                            {s.source && <Chip label={s.source} size="small" variant="outlined" />}
                          </Stack>
                          {s.evidence && <Typography variant="body2" sx={{ color: '#4a5568', mt: 0.25 }}>{s.evidence}</Typography>}
                        </Box>
                        <Chip
                          size="small"
                          label={decision === 'accepted' ? 'Applied' : (autoApplyError ? 'Pending' : 'Applying…')}
                          color={decision === 'accepted' ? 'success' : 'default'}
                          variant="filled"
                        />
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )
          )}

          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button variant="contained" onClick={closeReviewModal}>Done</Button>
          </DialogActions>
        </Modal>
      )}

      {/* CONVERT MODAL */}
      {convertModal && (
        <Modal title="Convert Lead to Customer" onClose={() => setConvertModal(false)}>
          <form onSubmit={handleConvert}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f7fafc' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 1 }}>{lead.companyName}</Typography>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Status: <strong>{lead.status}</strong></Typography>
                {lead.azotteFitScore != null && <Typography variant="body2" color="text.secondary">Fit Score: <strong>{lead.azotteFitScore}</strong></Typography>}
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary"><strong>What will be copied:</strong></Typography>
                <Box component="ul" sx={{ m: '4px 0 0', pl: 2.5, fontSize: '0.85rem' }}>
                  <li>Profile data ({lead.profileData ? Object.keys(JSON.parse(lead.profileData) || {}).length : 0} sections)</li>
                  <li>{lead.contacts?.length || 0} contacts</li>
                </Box>
              </Box>
            </Paper>
            <Stack spacing={2}>
              <TextField size="small" fullWidth multiline rows={2} label="Notes (optional)" value={convertNotes} onChange={e => setConvertNotes(e.target.value)} placeholder="Any notes about the conversion..." />
              <TextField size="small" fullWidth type="number" label="Link to Existing Customer ID (optional)" value={convertExistingId} onChange={e => setConvertExistingId(e.target.value)} placeholder="Leave empty to create a new customer" />
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setConvertModal(false)}>Cancel</Button>
              <Button variant="contained" type="submit">Convert to Customer</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* Quick note modal */}
      {quickNoteOpen && (
        <Modal open onClose={() => setQuickNoteOpen(false)} title="Add note">
          <TextField
            autoFocus fullWidth multiline rows={5}
            placeholder="What's worth remembering about this lead?"
            value={quickNoteText}
            onChange={e => setQuickNoteText(e.target.value)}
          />
          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button variant="outlined" onClick={() => setQuickNoteOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleQuickNoteSave} disabled={!quickNoteText.trim() || quickNoteSaving}>
              {quickNoteSaving ? 'Saving…' : 'Save note'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* Task create modal */}
      {taskModal && (
        <Modal open onClose={() => setTaskModal(null)} title={`New task: ${TASK_TYPE_LABELS[taskForm.type] || taskForm.type}`}>
          <Stack spacing={2}>
            <TextField select size="small" label="Type" value={taskForm.type}
              onChange={e => setTaskForm({ ...taskForm, type: e.target.value })}>
              {Object.entries(TASK_TYPE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Title" value={taskForm.title}
              placeholder={TASK_TYPE_LABELS[taskForm.type]}
              onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} />
            <TextField size="small" label="Description (optional)" multiline rows={3}
              value={taskForm.description}
              onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} />
            <TextField size="small" fullWidth type="datetime-local" label="Due date (optional)"
              slotProps={{ inputLabel: { shrink: true } }}
              value={taskForm.dueDate}
              onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })} />
            <TextField select size="small" label="Assigned to"
              value={taskForm.assignedToUserId}
              onChange={e => setTaskForm({ ...taskForm, assignedToUserId: e.target.value })}>
              <MenuItem value="">Unassigned</MenuItem>
              {allUsers.map(u => <MenuItem key={u.id} value={String(u.id)}>{u.fullName || u.username}</MenuItem>)}
            </TextField>
          </Stack>
          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button variant="outlined" onClick={() => setTaskModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleTaskSave} disabled={taskSaving}>
              {taskSaving ? 'Saving…' : 'Create task'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* Complete task dialog */}
      {completeTaskDialog && (
        <Modal open onClose={() => setCompleteTaskDialog(null)} title="Complete task">
          <Typography sx={{ mb: 1 }}>{completeTaskDialog.task.title}</Typography>
          {['SendEmail', 'LinkedInOutreach', 'XingOutreach', 'Call', 'Meeting'].includes(completeTaskDialog.task.type) && (
            <FormControlLabel
              control={<Checkbox checked={completeLogActivity}
                onChange={e => setCompleteLogActivity(e.target.checked)} />}
              label="Log this as an activity (creates an interaction record)"
            />
          )}
          <TextField fullWidth size="small" multiline rows={3} label="Notes (optional)"
            placeholder="Outcome, next steps, key takeaways…"
            value={completeNotes}
            onChange={e => setCompleteNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button variant="outlined" onClick={() => setCompleteTaskDialog(null)}>Cancel</Button>
            <Button variant="contained" startIcon={<TaskAltIcon />} onClick={handleCompleteConfirm}>
              Mark done
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* Tag dialog (mobile) */}
      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="e.g. FTA, OTT, SVOD"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); setTagDialogOpen(false); }
            }}
            disabled={tagSaving}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTagInput(''); setTagDialogOpen(false); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={tagSaving || !tagInput.trim()}
            onClick={() => { handleAddTag(); setTagDialogOpen(false); }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info dialog (mobile) */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Lead info</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Lead Score</Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: lead.azotteFitScore == null ? '#94a3b8' : lead.azotteFitScore >= 75 ? '#16a34a' : lead.azotteFitScore >= 50 ? '#d97706' : '#dc2626' }}>
                  {lead.azotteFitScore ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">/100</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Last enriched: {(lead.enrichmentCompleteness ?? 0) >= 50 ? `${lead.enrichmentCompleteness}% complete` : 'Not yet'}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">Owner</Typography>
              <Typography sx={{ fontWeight: 600, fontStyle: lead.assignedToName ? 'normal' : 'italic', color: lead.assignedToName ? '#0f172a' : '#94a3b8' }}>
                {lead.assignedToName || 'Unassigned'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Source</Typography>
              <Typography sx={{ fontWeight: 600 }}>{lead.source}{lead.sourceDetail ? ` · ${lead.sourceDetail}` : ''}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Region</Typography>
              <Typography sx={{ fontWeight: 600 }}>{lead.country || lead.state || lead.city || 'Unknown'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Last activity</Typography>
              <Typography sx={{ fontWeight: 600 }}>{formatRelative(lead.updatedAt)}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Find contacts progress dialog — blocks page interaction during search */}
      <Dialog open={discovering} disableEscapeKeyDown maxWidth="xs" fullWidth>
        <DialogTitle>Searching for contacts</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', py: 1 }}>
            <CircularProgress size={28} />
            <Box>
              <Typography variant="body2">Discovering contacts via selected providers…</Typography>
              <Typography variant="caption" color="text.secondary">This can take up to a minute.</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscover} variant="outlined">Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Outreach Timeline tab (per-lead rollup) ───
function OutreachTimelineTab({ leadId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await getLeadOutreachTimeline(leadId);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load timeline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  if (loading) return <Box sx={{ p: 2, color: '#94a3b8' }}>Loading…</Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return null;

  const items = data.items || [];

  const markerColor = (it) => {
    if (it.direction === 'Outbound') return '#1976d2';
    if (it.direction === 'Inbound') {
      if (it.aiIntent === 'Interested') return '#2e7d32';
      if (it.aiIntent === 'NotRelevant' || it.aiIntent === 'Unsubscribe') return '#c62828';
      return '#666';
    }
    return '#94a3b8';
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Total sent</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>{data.totalSent ?? 0}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Total replied</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>{data.totalReplied ?? 0}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Last interaction</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.lastInteractionAt ? new Date(data.lastInteractionAt).toLocaleString() : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Intent breakdown</Typography>
            <Stack direction="row" spacing={0.5}>
              {Object.entries(data.byIntent || {}).map(([k, v]) => (
                <Chip key={k} size="small" label={`${k}: ${v}`} variant="outlined" />
              ))}
              {Object.keys(data.byIntent || {}).length === 0 && <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: '#475569' }}>Chronological timeline</Typography>
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No interactions yet.</Typography>
        ) : (
          <Box sx={{ position: 'relative', pl: 2.5, borderLeft: '2px solid #e2e8f0' }}>
            {items.map((it) => (
              <Box key={it.id} sx={{ position: 'relative', mb: 2.5 }}>
                <Box sx={{ position: 'absolute', left: -28, top: 4, width: 14, height: 14, borderRadius: '50%', bgcolor: markerColor(it), border: '2px solid #fff', boxShadow: '0 0 0 2px ' + markerColor(it) }} />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.subject || '(no subject)'}</Typography>
                  <Chip size="small" label={it.direction} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  {it.direction === 'Inbound' && it.aiIntent && (
                    <Chip size="small" label={it.aiIntent} sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                  {it.direction === 'Outbound' && typeof it.aiQualityScore === 'number' && (
                    <Chip size="small" label={`Quality ${it.aiQualityScore}`} sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  {it.contactName ? `${it.contactName} · ` : ''}{it.interactionDate ? new Date(it.interactionDate).toLocaleString() : ''}
                </Typography>
                {it.bodyPreview && (
                  <Typography variant="body2" sx={{ color: '#475569', mt: 0.5, whiteSpace: 'pre-wrap' }}>{it.bodyPreview}</Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
